import React, { useEffect, useState } from "react";
import axios from "axios";
import { App, Button, Input, Modal, Popconfirm, Progress, Select, Spin, Table, Tabs, Tag, Upload } from "antd";
import {
  ArrowDownOutlined,
  ArrowUpOutlined,
  CopyOutlined,
  DeleteOutlined,
  DownloadOutlined,
  EditOutlined,
  EyeOutlined,
  PlusOutlined,
  PoweroffOutlined,
  UploadOutlined,
} from "@ant-design/icons";
import { useNavigate, useParams } from "react-router-dom";
import { downloadAuthenticatedFile, safeFileName } from "../../utils/downloadFile";
import "./DashboardBusiness.css";

const BUSINESS_PAGE_COPY = {
  "dynamic-qr": {
    title: "Dynamic QR Code",
    description: "Manage editable QR codes that can be updated after sharing.",
  },
  pages: {
    title: "API's",
    description: "Manage API keys for business integrations across supported modules.",
  },
  access: {
    title: "Access Management",
    description: "Manage Dynamic QR staff access, roles, assignments, and analytics reports.",
  },
};

const initialForm = {
  couponName: "",
  serviceLabel: "",
  creationType: "Single",
  couponCode: "",
  batchName: "",
  numberOfCoupons: "",
  prefix: "",
  codeFormat: "Random",
  usageLimit: "1",
  startDate: "",
  expiryDate: "",
  notesTerms: "",
};

const initialDynamicQrForm = {
  qrCodeName: "",
  dynamicQrType: "Master",
  qrOption: "Link",
  creationType: "Single",
  linkUrl: "",
  couponMappingType: "Single",
  couponIds: [],
  couponBatchId: "",
  batchName: "",
  numberOfQrCodes: "",
  expiresAt: "",
  scanLimit: "",
  size: "Medium",
  color: "#000000",
  backgroundColor: "#FFFFFF",
  appPlayStoreLink: "",
  appAppleAppStoreLink: "",
  websiteLink: "",
  heading: "",
  subheading: "",
  usageInstructions: [],
  extraLinks: [],
  headingLogoUrl: "",
  headingLogoFile: null,
  headingCardBackground: "#F5F7FB",
  headingTextColor: "#111827",
  subheadingTextColor: "#6B7280",
  assignedStaffIds: [],
};

const ACCESS_PERMISSION_OPTIONS = [
  { value: "dynamic_qr.view", label: "View Dynamic QR" },
  { value: "dynamic_qr.create", label: "Create Dynamic QR" },
  { value: "dynamic_qr.update", label: "Update Dynamic QR" },
  { value: "dynamic_qr.delete", label: "Delete Dynamic QR" },
  { value: "dynamic_qr.analytics.view", label: "View Analytics" },
  { value: "dynamic_qr.assign_staff", label: "Assign Staff" },
];

const initialStaffForm = {
  firstName: "",
  lastName: "",
  mobileNumber: "",
  email: "",
  password: "",
  autoGeneratePassword: false,
  forceChangePassword: true,
  dateOfBirth: "",
  dateOfJoining: "",
  address: "",
  pinCode: "",
  city: "",
  state: "",
  roleIds: [],
};

const initialRoleForm = {
  roleName: "",
  description: "",
  permissionKeys: [],
};

const INDIAN_STATES = [
  "Andhra Pradesh",
  "Arunachal Pradesh",
  "Assam",
  "Bihar",
  "Chhattisgarh",
  "Delhi",
  "Goa",
  "Gujarat",
  "Haryana",
  "Himachal Pradesh",
  "Jharkhand",
  "Karnataka",
  "Kerala",
  "Madhya Pradesh",
  "Maharashtra",
  "Odisha",
  "Punjab",
  "Rajasthan",
  "Tamil Nadu",
  "Telangana",
  "Uttar Pradesh",
  "Uttarakhand",
  "West Bengal",
].map((value) => ({ value, label: value }));

const apiBase = process.env.REACT_APP_BASE_URL;

const authHeaders = () => ({
  Authorization: `Bearer ${localStorage.getItem("token") || ""}`,
});

const getApiMessage = (error, fallback) =>
  error?.response?.data?.message || error?.message || fallback;

const getStoredUser = () => JSON.parse(localStorage.getItem("user") || "{}");

const getStoredPermissions = () => {
  const user = getStoredUser();
  const permissions = user.permissions || user.Permissions || user.data?.permissions || user.data?.Permissions || [];
  return Array.isArray(permissions) ? permissions : [];
};

const isStaffUser = () => String(getStoredUser().userType || getStoredUser().UserType || "").toLowerCase() === "staff";

const hasDynamicPermission = (permissionKey) => {
  if (!isStaffUser()) return true;
  return getStoredPermissions().includes(permissionKey);
};

const formatDate = (value) => {
  if (!value) return "-";
  const parsed = new Date(value);
  return Number.isNaN(parsed.getTime()) ? value : parsed.toLocaleDateString("en-IN", {
    day: "2-digit",
    month: "short",
    year: "numeric",
  });
};

const formatScanLimit = (value) => {
  if (value === null || value === undefined || value === "" || Number(value) === 0) {
    return "Unlimited";
  }
  return value;
};

const formatRemainingScans = (value) => {
  if (value === null || value === undefined || value === "") return "Unlimited";
  return value;
};

const toScanLimitPayload = (value) => {
  if (value === null || value === undefined || value === "") return null;
  const parsed = Number(value);
  if (!Number.isFinite(parsed) || parsed <= 0) return null;
  return parsed;
};

const toDateInput = (value) => {
  if (!value) return "";
  const parsed = new Date(value);
  if (Number.isNaN(parsed.getTime())) return String(value).slice(0, 10);
  return parsed.toISOString().slice(0, 10);
};

const toApiDate = (value, endOfDay = false) => {
  if (!value) return null;
  return `${value}T${endOfDay ? "23:59:59" : "00:00:00"}Z`;
};

const toSelectValue = (value) => (value === null || value === undefined ? "" : String(value));

const getUniqueSelectValues = (values = []) => {
  const seen = new Set();
  return values
    .map(toSelectValue)
    .filter(Boolean)
    .filter((value) => {
      if (seen.has(value)) return false;
      seen.add(value);
      return true;
    });
};

const extractDynamicCouponIds = (record = {}) => {
  const directIds = Array.isArray(record.couponIds) ? record.couponIds : [];
  const mappedCoupons = [
    record.couponMappings,
    record.coupons,
    record.mappedCoupons,
    record.dynamicQrCouponMappings,
  ]
    .filter(Array.isArray)
    .flat()
    .map((coupon) => coupon?.couponId ?? coupon?.id);

  return getUniqueSelectValues([...directIds, ...mappedCoupons]);
};

const extractDynamicCouponBatchId = (record = {}) =>
  toSelectValue(
    record.couponBatchId ??
    record.mappedCouponBatchId ??
    record.couponBatch?.batchId ??
    record.couponBatch?.id ??
    record.mappedCouponBatch?.batchId ??
    record.mappedCouponBatch?.id
  );

const getDynamicLandingPageFields = (record = {}) => {
  const landingPage = record.landingPage || {};
  const usageInstructions = Array.isArray(record.usageInstructions)
    ? record.usageInstructions
    : Array.isArray(landingPage.usageInstructions)
      ? landingPage.usageInstructions
      : [];
  const extraLinks = Array.isArray(record.extraLinks)
    ? record.extraLinks
    : Array.isArray(landingPage.extraLinks)
      ? landingPage.extraLinks
      : [];

  return {
    appPlayStoreLink: record.appPlayStoreLink ?? landingPage.appPlayStoreLink ?? "",
    appAppleAppStoreLink: record.appAppleAppStoreLink ?? landingPage.appAppleAppStoreLink ?? "",
    websiteLink: record.websiteLink ?? landingPage.websiteLink ?? "",
    heading: record.heading ?? landingPage.heading ?? "",
    subheading: record.subheading ?? landingPage.subheading ?? "",
    usageInstructions: usageInstructions.map((item) => ({
      title: String(item?.title || ""),
      description: String(item?.description || ""),
    })),
    extraLinks: extraLinks.map((item) => ({
      label: String(item?.label || ""),
      value: String(item?.value || ""),
      type: String(item?.type || ""),
    })),
    headingLogoUrl: record.headingLogoUrl ?? landingPage.headingLogoUrl ?? "",
    headingCardBackground: record.headingCardBackground ?? landingPage.headingCardBackground ?? "#F5F7FB",
    headingTextColor: record.headingTextColor ?? landingPage.headingTextColor ?? "#111827",
    subheadingTextColor: record.subheadingTextColor ?? landingPage.subheadingTextColor ?? "#6B7280",
  };
};

const applyDynamicLandingPagePayload = (payload, form) => {
  [
    "appPlayStoreLink",
    "appAppleAppStoreLink",
    "websiteLink",
    "heading",
    "subheading",
    "headingCardBackground",
    "headingTextColor",
    "subheadingTextColor",
  ].forEach((key) => {
    const value = form[key];
    if (typeof value === "string" ? value.trim() : value) {
      payload[key] = typeof value === "string" ? value.trim() : value;
    }
  });

  const usageInstructions = Array.isArray(form.usageInstructions)
    ? form.usageInstructions
        .map((item) => ({
          title: String(item?.title || "").trim(),
          description: String(item?.description || "").trim(),
        }))
        .filter((item) => item.title || item.description)
    : [];

  payload.usageInstructions = usageInstructions;

  const extraLinks = Array.isArray(form.extraLinks)
    ? form.extraLinks
        .map((item) => ({
          label: String(item?.label || "").trim(),
          value: String(item?.value || "").trim(),
          type: String(item?.type || "").trim(),
        }))
        .filter((item) => item.label || item.value || item.type)
    : [];

  payload.extraLinks = extraLinks;

  return payload;
};

const isValidHttpUrl = (value = "") => /^https?:\/\//i.test(String(value).trim());

const validateOptionalUrl = (value, label) => {
  const url = String(value || "").trim();
  if (!url) return "";
  return isValidHttpUrl(url) ? "" : `${label} must start with http:// or https://.`;
};

const validateExtraLinks = (extraLinks = []) => {
  if (!Array.isArray(extraLinks)) return "";

  for (const link of extraLinks) {
    const label = String(link?.label || "").trim();
    const value = String(link?.value || "").trim();
    const type = String(link?.type || "").trim();
    const hasAnyValue = label || value || type;

    if (value && !label) return "Extra Link label is required when value is added.";
    if (label && !value) return "Extra Link value is required when label is added.";
    if (hasAnyValue && !type) return "Extra Link type is required.";
    if (value && !isValidHttpUrl(value)) {
      return "Extra link URLs must start with http:// or https://.";
    }
  }

  return "";
};

const normalizeCouponRecord = (record = {}) => {
  const recordType = record.recordType || record.creationType || "Single";
  const isBatch = recordType === "Batch";

  return {
    ...record,
    id: record.id ?? record.couponId ?? record.batchId,
    batchId: record.batchId,
    rowType: recordType,
    creationType: record.creationType || recordType,
    couponName: record.couponName || "",
    serviceLabel: record.serviceLabel || "",
    couponCode: record.couponCode || "",
    batchName: record.batchName || "",
    notesTerms: record.notesTerms || "",
    couponCount: record.totalCoupons ?? record.couponCount ?? 0,
    usageLimit: isBatch
      ? record.totalUsageLimit ?? record.usageLimit ?? 0
      : record.usageLimit ?? 0,
    usedCount: record.usedCount ?? 0,
    activeCount: record.activeCount ?? 0,
    inactiveCount: record.inactiveCount ?? 0,
    startDate: record.startDate || "",
    expiryDate: record.expiryDate || "",
    status: record.batchStatus || record.status || "Active",
  };
};

const normalizeDynamicQrRecord = (record = {}) => {
  const recordType = record.recordType || record.creationType || "Single";
  const isBatch = recordType === "Batch";

  return {
    ...record,
    id: record.id ?? record.dynamicQrId ?? record.batchId,
    batchId: record.batchId,
    rowType: recordType,
    creationType: record.creationType || recordType,
    qrCodeName: record.qrCodeName || "",
    batchName: record.batchName || "",
    dynamicQrType: record.dynamicQrType || "",
    qrOption: record.qrOption || "",
    linkUrl: record.linkUrl || "",
    couponMappingType: record.couponMappingType || (extractDynamicCouponBatchId(record) ? "Batch" : "Single"),
    couponIds: extractDynamicCouponIds(record),
    couponBatchId: extractDynamicCouponBatchId(record),
    qrCount: record.totalQrCodes ?? record.qrCount ?? 0,
    scanCount: record.scanCount ?? record.totalScans ?? 0,
    scanLimit: record.scanLimit ?? null,
    remainingScans: record.remainingScans ?? null,
    isScanLimitReached: Boolean(record.isScanLimitReached),
    couponAppliedCount: record.couponAppliedCount ?? 0,
    couponCopiedCount: record.couponCopyCount ?? record.couponCopiedCount ?? 0,
    qrImageUrl: record.qrImageUrl || record.qrPngUrl || record.imageUrl || "",
    qrSvgUrl: record.qrSvgUrl || record.svgUrl || "",
    qrUrl: record.qrUrl || record.publicUrl || record.scanUrl || "",
    ...getDynamicLandingPageFields(record),
    landingPage: {
      ...getDynamicLandingPageFields(record),
      ...(record.landingPage || {}),
    },
    expiresAt: record.expiresAt || "",
    status: record.batchStatus || record.status || "Active",
    displayName: isBatch ? record.batchName || record.qrCodeName : record.qrCodeName,
  };
};

const getDynamicQrFormFromRecord = (record = {}) => ({
  qrCodeName: record.qrCodeName || record.displayName || "",
  dynamicQrType: record.qrOption && record.qrOption !== "Blank" ? "Master" : record.dynamicQrType || "Master",
  qrOption: record.qrOption || "Link",
  creationType: record.rowType || record.creationType || "Single",
  linkUrl: record.linkUrl || "",
  couponMappingType: record.couponMappingType || (extractDynamicCouponBatchId(record) ? "Batch" : "Single"),
  couponIds: extractDynamicCouponIds(record),
  couponBatchId: extractDynamicCouponBatchId(record),
  batchName: record.batchName || "",
  numberOfQrCodes: "",
  expiresAt: toDateInput(record.expiresAt),
  scanLimit: record.scanLimit && Number(record.scanLimit) > 0 ? String(record.scanLimit) : "",
  size: record.size || "Medium",
  color: record.color || "#000000",
  backgroundColor: record.backgroundColor || "#FFFFFF",
  ...getDynamicLandingPageFields(record),
  headingLogoFile: null,
});

const getCouponPayload = (form) => {
  const isBatch = form.creationType === "Batch";
  const payload = {
    couponName: form.couponName.trim(),
    serviceLabel: form.serviceLabel.trim(),
    creationType: form.creationType,
    usageLimit: Number(form.usageLimit || 1),
    startDate: toApiDate(form.startDate),
    expiryDate: toApiDate(form.expiryDate, true),
    notesTerms: form.notesTerms.trim(),
  };

  if (isBatch) {
    return {
      ...payload,
      batchName: form.batchName.trim(),
      numberOfCoupons: Number(form.numberOfCoupons),
      prefix: form.prefix.trim(),
      codeFormat: form.codeFormat,
    };
  }

  return {
    ...payload,
    couponCode: form.couponCode.trim().toUpperCase(),
  };
};

const getBatchPayload = (form) => ({
  couponName: form.couponName.trim(),
  serviceLabel: form.serviceLabel.trim(),
  batchName: form.batchName.trim(),
  usageLimitPerCoupon: Number(form.usageLimit || 1),
  startDate: toApiDate(form.startDate),
  expiryDate: toApiDate(form.expiryDate, true),
  notesTerms: form.notesTerms.trim(),
});

const getDynamicQrPayload = (form) => {
  const isBlank = form.qrOption === "Blank";
  const isBatch = form.creationType === "Batch";
  const payload = {
    dynamicQrType: isBlank ? "Blank" : "Master",
    creationType: form.creationType,
    qrOption: isBlank ? "Blank" : form.qrOption,
    qrCodeName: form.qrCodeName.trim(),
    expiresAt: toApiDate(form.expiresAt, true),
    scanLimit: toScanLimitPayload(form.scanLimit),
    size: form.size,
    color: form.color,
    backgroundColor: form.backgroundColor,
  };

  applyDynamicLandingPagePayload(payload, form);

  if (isBatch) {
    payload.batchName = form.batchName.trim();
    payload.numberOfQrCodes = Number(form.numberOfQrCodes);
  }

  if (isBlank) {
    return payload;
  }

  if (form.qrOption === "Link") {
    payload.linkUrl = form.linkUrl.trim();
    return payload;
  }

  payload.couponMappingType = form.couponMappingType;
  if (form.couponMappingType === "Batch") {
    payload.couponBatchId = form.couponBatchId;
  } else {
    payload.couponIds = form.couponIds.map(Number);
  }

  return payload;
};

const getDynamicQrUpdatePayload = (form) => {
  const payload = getDynamicQrPayload(form);
  delete payload.creationType;
  delete payload.numberOfQrCodes;
  return payload;
};

const getDynamicQrBatchUpdatePayload = (form) => {
  const payload = getDynamicQrPayload({
    ...form,
    creationType: "Batch",
  });

  delete payload.numberOfQrCodes;
  payload.creationType = "Batch";

  if (payload.qrOption === "CouponMapping") {
    if (payload.couponMappingType === "Single") {
      payload.couponIds = form.couponIds.map(Number);
      payload.couponBatchId = null;
      payload.mappedCouponBatchId = null;
    } else {
      payload.couponIds = [];
      payload.couponBatchId = form.couponBatchId;
      payload.mappedCouponBatchId = form.couponBatchId;
    }
  }

  return payload;
};

const getDynamicQrRequestBody = (payload, form) => {
  if (!form.headingLogoFile) {
    return { body: payload, headers: authHeaders() };
  }

  const formData = new FormData();
  Object.entries(payload).forEach(([key, value]) => {
    if (key === "scanLimit" && (value === null || value === undefined || value === "")) {
      formData.append(key, "0");
      return;
    }
    if (value === null || value === undefined || value === "") return;
    if (Array.isArray(value) || typeof value === "object") {
      formData.append(key, JSON.stringify(value));
      return;
    }
    formData.append(key, String(value));
  });
  formData.append("headingLogo", form.headingLogoFile);
  return { body: formData, headers: authHeaders() };
};

const CouponsPage = () => {
  const { message } = App.useApp();
  const navigate = useNavigate();
  const [coupons, setCoupons] = useState([]);
  const [form, setForm] = useState(initialForm);
  const [modalOpen, setModalOpen] = useState(false);
  const [viewCoupon, setViewCoupon] = useState(null);
  const [editingId, setEditingId] = useState(null);
  const [loading, setLoading] = useState(false);
  const [pagination, setPagination] = useState({ current: 1, pageSize: 20, total: 0 });

  const isBatch = form.creationType === "Batch";
  const listingRows = coupons;

  const fetchCoupons = async (page = pagination.current, pageSize = pagination.pageSize) => {
    setLoading(true);
    try {
      const response = await axios.get(`${apiBase}/api/Coupons`, {
        headers: authHeaders(),
        params: { page, pageSize },
      });
      const result = response.data?.data || {};
      const rows = Array.isArray(result.data) ? result.data : [];
      setCoupons(rows.map(normalizeCouponRecord));
      setPagination({
        current: result.page || page,
        pageSize: result.pageSize || pageSize,
        total: result.total || rows.length,
      });
    } catch (error) {
      message.error(getApiMessage(error, "Unable to fetch coupons."));
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchCoupons(1, pagination.pageSize);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const resetForm = () => {
    setForm(initialForm);
    setEditingId(null);
  };

  const updateForm = (key, value) => {
    setForm((prev) => ({ ...prev, [key]: value }));
  };

  const validateForm = () => {
    if (!form.couponName.trim()) return "Coupon Name is mandatory.";
    if (!form.serviceLabel.trim()) return "Service Label is mandatory.";
    if (!form.creationType) return "Creation Type is mandatory.";
    if (!isBatch && !form.couponCode.trim()) return "Coupon Code is mandatory.";
    if (form.startDate && form.expiryDate && form.expiryDate < form.startDate) {
      return "Expiry Date should not be earlier than Start Date.";
    }

    if (isBatch) {
      if (!form.batchName.trim()) return "Batch Name is mandatory.";
      if (!editingId && (!Number(form.numberOfCoupons) || Number(form.numberOfCoupons) < 1)) {
        return "Number of Coupons is mandatory.";
      }
      if (!form.codeFormat) return "Code Format is mandatory.";
      if (!Number(form.usageLimit) || Number(form.usageLimit) < 1) {
        return "Usage Limit Per Coupon is mandatory.";
      }
    }

    return "";
  };

  const handleSave = async () => {
    const validationError = validateForm();
    if (validationError) {
      message.warning(validationError);
      return;
    }

    try {
      if (editingId) {
        await axios.put(`${apiBase}/api/Coupons/${editingId}`, getCouponPayload(form), {
          headers: authHeaders(),
        });
        message.success("Coupon updated.");
      }

      setModalOpen(false);
      resetForm();
      fetchCoupons(pagination.current, pagination.pageSize);
    } catch (error) {
      message.error(getApiMessage(error, "Unable to save coupon."));
    }
  };

  const fetchSingleCoupon = async (coupon) => {
    try {
      const response = await axios.get(`${apiBase}/api/Coupons/${coupon.id}`, {
        headers: authHeaders(),
      });
      return normalizeCouponRecord(response.data?.data || coupon);
    } catch {
      return coupon;
    }
  };

  const handleView = async (record) => {
    if (record.rowType === "Batch") {
      navigate(`/dashboard/business/coupons/batch/${encodeURIComponent(record.batchId)}`);
      return;
    }
    setViewCoupon(await fetchSingleCoupon(record));
  };

  const handleEdit = async (coupon) => {
    const detail = await fetchSingleCoupon(coupon);
    setEditingId(detail.id);
    setForm({
      couponName: detail.couponName,
      serviceLabel: detail.serviceLabel,
      creationType: detail.creationType,
      couponCode: detail.couponCode,
      batchName: detail.batchName || "",
      numberOfCoupons: "",
      prefix: "",
      codeFormat: "Random",
      usageLimit: String(detail.usageLimit),
      startDate: toDateInput(detail.startDate),
      expiryDate: toDateInput(detail.expiryDate),
      notesTerms: detail.notesTerms || "",
    });
    setModalOpen(true);
  };

  const toggleStatus = async (coupon) => {
    const isActive = coupon.status === "Active" || coupon.status === "Mixed";
    const action = isActive ? "deactivate" : "activate";
    const endpoint = coupon.rowType === "Batch"
      ? `${apiBase}/api/Coupons/batches/${coupon.batchId}/${action}`
      : `${apiBase}/api/Coupons/${coupon.id}/${action}`;

    try {
      await axios.post(endpoint, {}, { headers: authHeaders() });
      message.success(coupon.rowType === "Batch" ? "Batch status updated." : "Coupon status updated.");
      fetchCoupons(pagination.current, pagination.pageSize);
    } catch (error) {
      message.error(getApiMessage(error, "Unable to update status."));
    }
  };

  const deleteCoupon = async (record) => {
    const endpoint = record.rowType === "Batch"
      ? `${apiBase}/api/Coupons/batches/${record.batchId}`
      : `${apiBase}/api/Coupons/${record.id}`;

    try {
      await axios.delete(endpoint, { headers: authHeaders() });
      message.success(record.rowType === "Batch" ? "Batch deleted." : "Coupon deleted.");
      fetchCoupons(pagination.current, pagination.pageSize);
    } catch (error) {
      message.error(getApiMessage(error, "Unable to delete coupon."));
    }
  };

  const handleBatchEdit = (batch) => {
    setEditingId(batch.batchId);
    setForm({
      couponName: batch.couponName,
      serviceLabel: batch.serviceLabel,
      creationType: "Batch",
      couponCode: "",
      batchName: batch.batchName || "",
      numberOfCoupons: "",
      prefix: "",
      codeFormat: "Random",
      usageLimit: String(batch.usageLimit && batch.couponCount ? Math.round(batch.usageLimit / batch.couponCount) : 1),
      startDate: toDateInput(batch.startDate),
      expiryDate: toDateInput(batch.expiryDate),
      notesTerms: batch.notesTerms || "",
    });
    setModalOpen(true);
  };

  const handleSaveBatch = async () => {
    const validationError = validateForm();
    if (validationError) {
      message.warning(validationError);
      return;
    }

    try {
      await axios.put(`${apiBase}/api/Coupons/batches/${editingId}`, getBatchPayload(form), {
        headers: authHeaders(),
      });
      message.success("Batch updated.");
      setModalOpen(false);
      resetForm();
      fetchCoupons(pagination.current, pagination.pageSize);
    } catch (error) {
      message.error(getApiMessage(error, "Unable to update batch."));
    }
  };

  const columns = [
    {
      title: "Name",
      dataIndex: "couponName",
      key: "couponName",
      render: (text, record) => (
        <div>
          <strong className="dbiz-table-title">
            {record.rowType === "Batch" ? record.batchName || text : text}
          </strong>
          <small className="dbiz-table-sub">
            {record.rowType === "Batch" ? `${record.couponName} · ${record.serviceLabel}` : record.serviceLabel}
          </small>
        </div>
      ),
    },
    {
      title: "Creation Type",
      dataIndex: "creationType",
      key: "creationType",
      render: (value) => <Tag color={value === "Batch" ? "purple" : "blue"}>{value}</Tag>,
    },
    {
      title: "Coupon Info",
      key: "couponInfo",
      render: (_, record) =>
        record.rowType === "Batch" ? (
          <span className="dbiz-muted">{record.couponCount} coupons</span>
        ) : (
          <strong className="dbiz-code">{record.couponCode}</strong>
        ),
    },
    {
      title: "Usage",
      key: "usage",
      render: (_, record) => (
        <span>{record.usedCount || 0} / {record.usageLimit || 0}</span>
      ),
    },
    { title: "Start Date", dataIndex: "startDate", key: "startDate", render: formatDate },
    { title: "Expiry Date", dataIndex: "expiryDate", key: "expiryDate", render: formatDate },
    {
      title: "Status",
      dataIndex: "status",
      key: "status",
      render: (status) => (
        <Tag color={status === "Active" ? "green" : status === "Mixed" ? "gold" : "red"}>
          {status}
        </Tag>
      ),
    },
    {
      title: "Actions",
      key: "actions",
      fixed: "right",
      render: (_, record) => (
        <div className="dbiz-actions" onClick={(event) => event.stopPropagation()}>
          <button
            type="button"
            className="view"
            onClick={() => handleView(record)}
            title="View"
          >
            <EyeOutlined />
          </button>
          <button
            type="button"
            className="edit"
            onClick={() => record.rowType === "Batch" ? handleBatchEdit(record) : handleEdit(record)}
            title="Edit"
          >
            <EditOutlined />
          </button>
          <button type="button" className={record.status === "Active" ? "danger" : "success"} onClick={() => toggleStatus(record)} title={record.status === "Active" ? "Deactivate" : "Activate"}><PoweroffOutlined /></button>
          <Popconfirm title={record.rowType === "Batch" ? "Delete this batch?" : "Delete this coupon?"} onConfirm={() => deleteCoupon(record)}>
            <button type="button" className="delete" title="Delete"><DeleteOutlined /></button>
          </Popconfirm>
        </div>
      ),
    },
  ];

  return (
    <div className="dbiz-root">
      <div className="dbiz-header">
        <div>
          <span className="dbiz-eyebrow">Business</span>
          <h1>Coupons</h1>
          <p>Create single coupons or generate coupon batches for your services.</p>
        </div>
        <Button type="primary" icon={<PlusOutlined />} onClick={() => navigate("/dashboard/business/coupons/new")}>
          Add New Coupon
        </Button>
      </div>

      <div className="dbiz-summary">
        <div><span>Total Coupons</span><strong>{coupons.length}</strong></div>
        <div><span>Active</span><strong>{coupons.filter((coupon) => coupon.status === "Active").length}</strong></div>
        <div><span>Used</span><strong>{coupons.reduce((sum, coupon) => sum + coupon.usedCount, 0)}</strong></div>
      </div>

      <div className="dbiz-table-card">
        <Table
          rowKey="id"
          columns={columns}
          dataSource={listingRows}
          loading={loading}
          scroll={{ x: 1100 }}
          pagination={{
            current: pagination.current,
            pageSize: pagination.pageSize,
            total: pagination.total,
          }}
          onChange={(nextPagination) => {
            fetchCoupons(nextPagination.current, nextPagination.pageSize);
          }}
          locale={{ emptyText: "No coupons created yet." }}
          rowClassName={(record) => record.rowType === "Batch" ? "dbiz-clickable-row" : ""}
          onRow={(record) => ({
            onClick: () => {
              if (record.rowType === "Batch") {
                navigate(`/dashboard/business/coupons/batch/${encodeURIComponent(record.batchId)}`);
              }
            },
          })}
        />
      </div>

      <Modal
        open={modalOpen}
        onCancel={() => { setModalOpen(false); resetForm(); }}
        onOk={editingId && isBatch ? handleSaveBatch : handleSave}
        okText={editingId ? "Update Coupon" : "Create Coupon"}
        width={760}
        title={editingId ? "Edit Coupon" : "Add New Coupon"}
        centered
      >
        <div className="dbiz-form-grid">
          <label>
            Coupon Name *
            <Input value={form.couponName} onChange={(e) => updateForm("couponName", e.target.value)} placeholder="Festival Offer" />
          </label>
          <label>
            Service Label *
            <Input value={form.serviceLabel} onChange={(e) => updateForm("serviceLabel", e.target.value)} placeholder="Spa Service" />
          </label>
          <label>
            Creation Type *
            <Select
              value={form.creationType}
              disabled={Boolean(editingId)}
              onChange={(value) => updateForm("creationType", value)}
              options={[{ value: "Single", label: "Single" }, { value: "Batch", label: "Batch" }]}
            />
          </label>
        </div>

        {isBatch && (
          <div className="dbiz-batch-box">
            <div className="dbiz-batch-title">Batch Settings</div>
            <div className="dbiz-form-grid">
              <label>
                Batch Name *
                <Input value={form.batchName} onChange={(e) => updateForm("batchName", e.target.value)} placeholder="Festival Campaign" />
              </label>
              {!editingId && (
                <>
                  <label>
                    Number of Coupons *
                    <Input type="number" min="1" value={form.numberOfCoupons} onChange={(e) => updateForm("numberOfCoupons", e.target.value)} placeholder="100" />
                  </label>
                  <label>
                    Prefix
                    <Input value={form.prefix} onChange={(e) => updateForm("prefix", e.target.value)} placeholder="SPA" />
                  </label>
                  <label>
                    Code Format *
                    <Select
                      value={form.codeFormat}
                      onChange={(value) => updateForm("codeFormat", value)}
                      options={[{ value: "Random", label: "Random" }, { value: "Sequential", label: "Sequential" }]}
                    />
                  </label>
                </>
              )}
              <label>
                Usage Limit Per Coupon *
                <Input type="number" min="1" value={form.usageLimit} onChange={(e) => updateForm("usageLimit", e.target.value)} />
              </label>
              <label>
                Start Date
                <Input type="date" value={form.startDate} onChange={(e) => updateForm("startDate", e.target.value)} />
              </label>
              <label>
                Expiry Date
                <Input type="date" value={form.expiryDate} onChange={(e) => updateForm("expiryDate", e.target.value)} />
              </label>
              <label className="dbiz-span-3">
                Notes / Terms
                <Input.TextArea
                  rows={3}
                  value={form.notesTerms}
                  onChange={(e) => updateForm("notesTerms", e.target.value)}
                  placeholder="Valid for new customers only. Cannot be combined with other offers."
                />
              </label>
            </div>
          </div>
        )}

        {!isBatch && (
          <div className="dbiz-form-grid dbiz-single-extra">
            <label>
              Usage Limit
              <Input type="number" min="1" value={form.usageLimit} onChange={(e) => updateForm("usageLimit", e.target.value)} />
            </label>
            <label>
              Start Date
              <Input type="date" value={form.startDate} onChange={(e) => updateForm("startDate", e.target.value)} />
            </label>
            <label>
              Expiry Date
              <Input type="date" value={form.expiryDate} onChange={(e) => updateForm("expiryDate", e.target.value)} />
            </label>
            <label className="dbiz-span-3">
              Notes / Terms
              <Input.TextArea
                rows={3}
                value={form.notesTerms}
                onChange={(e) => updateForm("notesTerms", e.target.value)}
                placeholder="Valid for new customers only. Cannot be combined with other offers."
              />
            </label>
          </div>
        )}
      </Modal>

      <Modal open={Boolean(viewCoupon)} onCancel={() => setViewCoupon(null)} footer={null} title={null} width={720} centered>
        {viewCoupon && (
          <div className="dbiz-coupon-modal">
            <div className="dbiz-coupon-hero">
              <div>
                <span className="dbiz-eyebrow">Single Coupon</span>
                <h2>{viewCoupon.couponName}</h2>
                <p>{viewCoupon.serviceLabel}</p>
              </div>
              <Tag color={viewCoupon.status === "Active" ? "green" : "red"}>{viewCoupon.status}</Tag>
            </div>

            <div className="dbiz-coupon-code-card">
              <span>Coupon Code</span>
              <strong>{viewCoupon.couponCode}</strong>
            </div>

            <div className="dbiz-coupon-usage">
              <div>
                <span>Usage</span>
                <strong>{viewCoupon.usedCount || 0} / {viewCoupon.usageLimit || 0}</strong>
              </div>
              <div className="dbiz-progress">
                <span style={{ width: `${Math.min(((viewCoupon.usedCount || 0) / (viewCoupon.usageLimit || 1)) * 100, 100)}%` }} />
              </div>
            </div>

            <div className="dbiz-coupon-info-grid">
              {[
                ["Creation Type", viewCoupon.creationType],
                ["Start Date", formatDate(viewCoupon.startDate)],
                ["Expiry Date", formatDate(viewCoupon.expiryDate)],
                ["Remaining Uses", Math.max((viewCoupon.usageLimit || 0) - (viewCoupon.usedCount || 0), 0)],
              ].map(([label, value]) => (
                <div key={label}>
                  <span>{label}</span>
                  <strong>{value}</strong>
                </div>
              ))}
            </div>
            {viewCoupon.notesTerms && (
              <div className="dbiz-coupon-notes">
                <span>Notes / Terms</span>
                <p>{viewCoupon.notesTerms}</p>
              </div>
            )}
          </div>
        )}
      </Modal>
    </div>
  );
};

const BusinessApiKeysPage = () => {
  const { message } = App.useApp();
  const navigate = useNavigate();
  const [apiKeys, setApiKeys] = useState([]);
  const [loading, setLoading] = useState(false);
  const [createOpen, setCreateOpen] = useState(false);
  const [docsOpen, setDocsOpen] = useState(false);
  const [keyName, setKeyName] = useState("");
  const [creating, setCreating] = useState(false);
  const [createdKey, setCreatedKey] = useState(null);

  const fetchApiKeys = async () => {
    setLoading(true);
    try {
      const response = await axios.get(`${apiBase}/api/Coupons/api-keys`, {
        headers: authHeaders(),
      });
      setApiKeys(unwrapArray(response.data));
    } catch (error) {
      message.error(getApiMessage(error, "Unable to fetch API keys."));
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchApiKeys();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const copyText = async (value, label) => {
    if (!value) return;
    try {
      await navigator.clipboard.writeText(value);
      message.success(`${label} copied.`);
    } catch {
      message.error(`Unable to copy ${label.toLowerCase()}.`);
    }
  };

  const createApiKey = async () => {
    if (!keyName.trim()) {
      message.warning("API key name is mandatory.");
      return;
    }

    setCreating(true);
    try {
      const response = await axios.post(
        `${apiBase}/api/Coupons/api-keys`,
        { name: keyName.trim() },
        { headers: authHeaders() }
      );
      const nextKey = response.data?.data || {};
      setCreatedKey(nextKey);
      setCreateOpen(false);
      setKeyName("");
      message.success("API key generated.");
      fetchApiKeys();
    } catch (error) {
      message.error(getApiMessage(error, "Unable to generate API key."));
    } finally {
      setCreating(false);
    }
  };

  const revokeApiKey = async (record) => {
    try {
      await axios.post(`${apiBase}/api/Coupons/api-keys/${record.id}/revoke`, {}, {
        headers: authHeaders(),
      });
      message.success("API key revoked.");
      fetchApiKeys();
    } catch (error) {
      message.error(getApiMessage(error, "Unable to revoke API key."));
    }
  };

  const activeKeyCount = apiKeys.filter((key) => key.status === "Active").length;
  const revokedKeyCount = apiKeys.filter((key) => key.status === "Revoked").length;
  const usedKeyCount = apiKeys.filter((key) => key.lastUsedAt).length;

  const columns = [
    {
      title: "Name",
      dataIndex: "name",
      key: "name",
      render: (value) => <strong className="dbiz-table-title">{value || "-"}</strong>,
    },
    {
      title: "Access Key",
      dataIndex: "accessKey",
      key: "accessKey",
      render: (value) => (
        <div className="dbiz-key-cell">
          <strong className="dbiz-code">{value || "-"}</strong>
          {value && (
            <Button size="small" icon={<CopyOutlined />} onClick={() => copyText(value, "Access Key")}>
              Copy
            </Button>
          )}
        </div>
      ),
    },
    { title: "Secret Last 4", dataIndex: "secretLast4", key: "secretLast4", render: (value) => value ? `•••• ${value}` : "-" },
    {
      title: "Status",
      dataIndex: "status",
      key: "status",
      render: (status) => <Tag color={status === "Active" ? "green" : "red"}>{status || "-"}</Tag>,
    },
    { title: "Created At", dataIndex: "createdAt", key: "createdAt", render: formatDate },
    { title: "Last Used At", dataIndex: "lastUsedAt", key: "lastUsedAt", render: formatDate },
    {
      title: "Actions",
      key: "actions",
      fixed: "right",
      render: (_, record) => (
        <div className="dbiz-actions">
          <button type="button" className="view" title="Copy Access Key" onClick={() => copyText(record.accessKey, "Access Key")}>
            <CopyOutlined />
          </button>
          {record.status === "Active" && (
            <Popconfirm title="Revoke this API key?" onConfirm={() => revokeApiKey(record)}>
              <button type="button" className="danger" title="Revoke"><PoweroffOutlined /></button>
            </Popconfirm>
          )}
        </div>
      ),
    },
  ];

  return (
    <div className="dbiz-root dbiz-api-page">
      <div className="dbiz-api-hero">
        <div>
          <span className="dbiz-eyebrow">Business API</span>
          <h1>Integration Keys</h1>
          <p>Generate secure keys for customer platform integrations. Today these keys support coupon applied events, and the same area can support more modules later.</p>
        </div>
        <div className="dbiz-header-actions">
          <Button onClick={() => navigate("/dashboard/home")}>Back to Dashboard</Button>
          <Button icon={<EyeOutlined />} onClick={() => setDocsOpen(true)}>
            API Docs
          </Button>
          <Button type="primary" icon={<PlusOutlined />} onClick={() => setCreateOpen(true)}>
            Generate API Key
          </Button>
        </div>
      </div>

      <div className="dbiz-api-summary">
        <div><span>Total Keys</span><strong>{apiKeys.length}</strong></div>
        <div><span>Active</span><strong>{activeKeyCount}</strong></div>
        <div><span>Used</span><strong>{usedKeyCount}</strong></div>
        <div><span>Revoked</span><strong>{revokedKeyCount}</strong></div>
      </div>

      <section className="dbiz-api-overview">
        <div className="dbiz-api-panel">
          <span className="dbiz-eyebrow">Supported Module</span>
          <h2>Coupons</h2>
          <p>Customer websites and apps can report coupon applied events through an authenticated server-to-server call.</p>
          <div className="dbiz-api-status-row">
            <Tag color="green">Available Now</Tag>
            <Tag color="blue">Server API</Tag>
          </div>
        </div>
        <div className="dbiz-api-panel dbiz-api-endpoint">
          <span className="dbiz-eyebrow">Event Endpoint</span>
          <h2>Coupon Applied</h2>
          <p>Use Access Key and Secret Key in request headers. Secret Keys should never be used in browser code.</p>
          <strong>POST /api/Coupons/events/apply</strong>
          <Button onClick={() => setDocsOpen(true)}>How to use API</Button>
        </div>
      </section>

      <div className="dbiz-table-card">
        <div className="dbiz-table-head">
          <div>
            <h2>API keys</h2>
            <p>Manage keys issued for business integrations. Secret Key is only visible when generated.</p>
          </div>
          <Button type="primary" icon={<PlusOutlined />} onClick={() => setCreateOpen(true)}>
            Generate Key
          </Button>
        </div>
        <Table
          rowKey="id"
          columns={columns}
          dataSource={apiKeys}
          loading={loading}
          scroll={{ x: 1100 }}
          pagination={{ pageSize: 10 }}
          locale={{ emptyText: "No API keys generated yet." }}
        />
      </div>

      <Modal
        open={createOpen}
        title="Generate Integration Key"
        okText="Generate Key"
        confirmLoading={creating}
        onOk={createApiKey}
        onCancel={() => {
          setCreateOpen(false);
          setKeyName("");
        }}
        centered
      >
        <label className="dbiz-modal-field">
          Key Name *
          <Input value={keyName} onChange={(e) => setKeyName(e.target.value)} placeholder="Website booking system" />
        </label>
      </Modal>

      <Modal
        open={docsOpen}
        title="Coupon API Docs"
        footer={<Button type="primary" onClick={() => setDocsOpen(false)}>Done</Button>}
        onCancel={() => setDocsOpen(false)}
        width={860}
        centered
      >
        <div className="dbiz-api-docs">
          <section>
            <h3>How to use API</h3>
            <p>Use these APIs from the customer's system to confirm when a coupon is actually applied or used.</p>
          </section>

          <section>
            <h3>Authentication</h3>
            <pre>{`X-Access-Key: <accessKey>
X-Secret-Key: <secretKey>`}</pre>
            <p>Business users can generate these keys from the Bonoxi dashboard.</p>
          </section>

          <section>
            <h3>Capture Coupon Applied Event</h3>
            <pre>{`POST https://api.bonoxi.com/api/Coupons/events/apply
Content-Type: application/json
X-Access-Key: bk_xxxxxxxxx
X-Secret-Key: bs_xxxxxxxxx`}</pre>
          </section>

          <section>
            <h3>Request</h3>
            <p>Use either <code>couponCode</code> or <code>couponId</code>.</p>
            <pre>{`{
  "couponCode": "SPA100",
  "coupon_applied": true,
  "externalReference": "booking-12345",
  "source": "website-booking",
  "metadata": {
    "customerId": "CUST-1001",
    "orderAmount": 1500,
    "currency": "INR"
  }
}`}</pre>
          </section>

          <section>
            <h3>Success Response</h3>
            <pre>{`{
  "data": {
    "eventId": 25,
    "duplicate": false,
    "coupon": {
      "id": 12,
      "couponName": "Summer Offer",
      "serviceLabel": "Spa",
      "couponCode": "SPA100",
      "usageLimit": 100,
      "usedCount": 8,
      "remainingUses": 92,
      "status": "Active",
      "isUsable": true,
      "unusableReason": null
    }
  },
  "message": "Coupon applied event captured successfully",
  "error": false
}`}</pre>
          </section>

          <section>
            <h3>Duplicate Response</h3>
            <p>If the same <code>externalReference</code> is sent again, usage count will not increase again.</p>
            <pre>{`{
  "data": {
    "eventId": 25,
    "duplicate": true,
    "coupon": {
      "id": 12,
      "couponCode": "SPA100",
      "usageLimit": 100,
      "usedCount": 8,
      "remainingUses": 92
    }
  },
  "message": "Coupon applied event already captured",
  "error": false
}`}</pre>
          </section>

          <section>
            <h3>Error Response Example</h3>
            <pre>{`{
  "data": {},
  "message": "Coupon usage limit reached",
  "error": true
}`}</pre>
            <div className="dbiz-doc-list">
              <span>Common errors</span>
              <ul>
                <li>Invalid API credentials</li>
                <li>Coupon not found</li>
                <li>Coupon is inactive</li>
                <li>Coupon has expired</li>
                <li>Coupon has not started yet</li>
                <li>Coupon usage limit reached</li>
                <li>couponApplied or coupon_applied must be true</li>
              </ul>
            </div>
          </section>

          <section className="dbiz-doc-important">
            <h3>Important Notes</h3>
            <ul>
              <li><code>externalReference</code> should be unique per booking/order.</li>
              <li>Send <code>coupon_applied: true</code> only after coupon is actually used in your system.</li>
              <li>Do not expose Secret Key in browser frontend code. This API should be called from the customer's backend/server.</li>
            </ul>
          </section>
        </div>
      </Modal>

      <Modal
        open={Boolean(createdKey)}
        title="API Key Generated"
        centered
        width={620}
        onCancel={() => setCreatedKey(null)}
        footer={[
          <Button key="copy-access" icon={<CopyOutlined />} onClick={() => copyText(createdKey?.accessKey, "Access Key")}>
            Copy Access Key
          </Button>,
          <Button key="copy-secret" icon={<CopyOutlined />} onClick={() => copyText(createdKey?.secretKey, "Secret Key")}>
            Copy Secret Key
          </Button>,
          <Button key="done" type="primary" onClick={() => setCreatedKey(null)}>
            Done
          </Button>,
        ]}
      >
        <div className="dbiz-generated-credentials dbiz-api-secret-modal">
          <div className="dbiz-secret-warning">
            <strong>Copy this Secret Key now.</strong>
            <span>It will not be shown again after this modal is closed.</span>
          </div>
          <div className="dbiz-generated-row">
            <span>Access Key</span>
            <strong>{createdKey?.accessKey}</strong>
            <Button size="small" icon={<CopyOutlined />} onClick={() => copyText(createdKey?.accessKey, "Access Key")} />
          </div>
          <div className="dbiz-generated-row">
            <span>Secret Key</span>
            <strong>{createdKey?.secretKey}</strong>
            <Button size="small" icon={<CopyOutlined />} onClick={() => copyText(createdKey?.secretKey, "Secret Key")} />
          </div>
          <div className="dbiz-form-help">
            Secret Key is stored only by the backend hash and is visible here one time only.
          </div>
        </div>
      </Modal>
    </div>
  );
};

const CouponCreatePage = () => {
  const { message } = App.useApp();
  const navigate = useNavigate();
  const [form, setForm] = useState(initialForm);
  const [creating, setCreating] = useState(false);
  const isBatch = form.creationType === "Batch";

  const updateForm = (key, value) => {
    setForm((prev) => ({ ...prev, [key]: value }));
  };

  const validateForm = () => {
    if (!form.couponName.trim()) return "Coupon Name is mandatory.";
    if (!form.serviceLabel.trim()) return "Service Label is mandatory.";
    if (!form.creationType) return "Creation Type is mandatory.";
    if (!isBatch && !form.couponCode.trim()) return "Coupon Code is mandatory.";
    if (form.startDate && form.expiryDate && form.expiryDate < form.startDate) {
      return "Expiry Date should not be earlier than Start Date.";
    }

    if (isBatch) {
      if (!form.batchName.trim()) return "Batch Name is mandatory.";
      if (!Number(form.numberOfCoupons) || Number(form.numberOfCoupons) < 1) {
        return "Number of Coupons is mandatory.";
      }
      if (!form.codeFormat) return "Code Format is mandatory.";
      if (!Number(form.usageLimit) || Number(form.usageLimit) < 1) {
        return "Usage Limit Per Coupon is mandatory.";
      }
    }

    return "";
  };

  const handleCreate = async () => {
    const validationError = validateForm();
    if (validationError) {
      message.warning(validationError);
      return;
    }

    setCreating(true);
    try {
      await axios.post(`${apiBase}/api/Coupons`, getCouponPayload(form), {
        headers: authHeaders(),
      });
      message.success(isBatch ? "Batch coupons created." : "Coupon created.");
      navigate("/dashboard/business/coupons");
    } catch (error) {
      message.error(getApiMessage(error, "Unable to create coupon."));
    } finally {
      setCreating(false);
    }
  };

  return (
    <div className="dbiz-root">
      <div className="dbiz-header dbiz-create-header">
        <div>
          <span className="dbiz-eyebrow">Business</span>
          <h1>Add New Coupon</h1>
          <p>Create a single coupon or generate a full batch of unique coupon codes.</p>
        </div>
        <Button onClick={() => navigate("/dashboard/business/coupons")}>
          Back to Coupons
        </Button>
      </div>

      <section className="dbiz-create-card">
        <div className="dbiz-form-section">
          <div className="dbiz-form-section-head">
            <span>Required</span>
            <h2>Coupon Details</h2>
          </div>
          <div className="dbiz-form-grid">
            <label>
              Coupon Name *
              <Input value={form.couponName} onChange={(e) => updateForm("couponName", e.target.value)} placeholder="Festival Offer" />
            </label>
            <label>
              Service Label *
              <Input value={form.serviceLabel} onChange={(e) => updateForm("serviceLabel", e.target.value)} placeholder="Spa Service" />
            </label>
            {!isBatch && (
              <label>
                Coupon Code *
                <Input value={form.couponCode} onChange={(e) => updateForm("couponCode", e.target.value.toUpperCase())} placeholder="SPA-A8K2L9" />
              </label>
            )}
            <label>
              Creation Type *
              <Select
                value={form.creationType}
                onChange={(value) => updateForm("creationType", value)}
                options={[{ value: "Single", label: "Single" }, { value: "Batch", label: "Batch" }]}
              />
            </label>
          </div>
        </div>

        {isBatch && (
          <div className="dbiz-form-section dbiz-batch-box">
            <div className="dbiz-form-section-head">
              <span>Batch</span>
              <h2>Batch Settings</h2>
            </div>
            <div className="dbiz-form-grid">
              <label>
                Batch Name *
                <Input value={form.batchName} onChange={(e) => updateForm("batchName", e.target.value)} placeholder="Festival Campaign" />
              </label>
              <label>
                Number of Coupons *
                <Input type="number" min="1" value={form.numberOfCoupons} onChange={(e) => updateForm("numberOfCoupons", e.target.value)} placeholder="100" />
              </label>
              <label>
                Prefix
                <Input value={form.prefix} onChange={(e) => updateForm("prefix", e.target.value)} placeholder="SPA" />
              </label>
              <label>
                Code Format *
                <Select
                  value={form.codeFormat}
                  onChange={(value) => updateForm("codeFormat", value)}
                  options={[{ value: "Random", label: "Random" }, { value: "Sequential", label: "Sequential" }]}
                />
              </label>
              <label>
                Usage Limit Per Coupon *
                <Input type="number" min="1" value={form.usageLimit} onChange={(e) => updateForm("usageLimit", e.target.value)} />
              </label>
              <label>
                Start Date
                <Input type="date" value={form.startDate} onChange={(e) => updateForm("startDate", e.target.value)} />
              </label>
              <label>
                Expiry Date
                <Input type="date" value={form.expiryDate} onChange={(e) => updateForm("expiryDate", e.target.value)} />
              </label>
              <label className="dbiz-span-3">
                Notes / Terms
                <Input.TextArea
                  rows={3}
                  value={form.notesTerms}
                  onChange={(e) => updateForm("notesTerms", e.target.value)}
                  placeholder="Valid once per customer. Cannot be combined with other offers."
                />
              </label>
            </div>
          </div>
        )}

        {!isBatch && (
          <div className="dbiz-form-section">
            <div className="dbiz-form-section-head">
              <span>Optional</span>
              <h2>Validity & Usage</h2>
            </div>
            <div className="dbiz-form-grid">
              <label>
                Usage Limit
                <Input type="number" min="1" value={form.usageLimit} onChange={(e) => updateForm("usageLimit", e.target.value)} />
              </label>
              <label>
                Start Date
                <Input type="date" value={form.startDate} onChange={(e) => updateForm("startDate", e.target.value)} />
              </label>
              <label>
                Expiry Date
                <Input type="date" value={form.expiryDate} onChange={(e) => updateForm("expiryDate", e.target.value)} />
              </label>
              <label className="dbiz-span-3">
                Notes / Terms
                <Input.TextArea
                  rows={3}
                  value={form.notesTerms}
                  onChange={(e) => updateForm("notesTerms", e.target.value)}
                  placeholder="Valid for new customers only. Cannot be combined with other offers."
                />
              </label>
            </div>
          </div>
        )}

        <div className="dbiz-create-actions">
          <Button onClick={() => navigate("/dashboard/business/coupons")}>Cancel</Button>
          <Button type="primary" icon={<PlusOutlined />} loading={creating} onClick={handleCreate}>
            {isBatch ? "Create Batch Coupons" : "Create Coupon"}
          </Button>
        </div>
      </section>
    </div>
  );
};

const CouponBatchDetailPage = () => {
  const { message } = App.useApp();
  const navigate = useNavigate();
  const { batchId = "" } = useParams();
  const decodedBatchId = decodeURIComponent(batchId);
  const [loading, setLoading] = useState(true);
  const [detail, setDetail] = useState(null);

  useEffect(() => {
    const fetchBatch = async () => {
      setLoading(true);
      try {
        const response = await axios.get(`${apiBase}/api/Coupons/batches/${decodedBatchId}`, {
          headers: authHeaders(),
        });
        setDetail(response.data?.data || null);
      } catch (error) {
        message.error(getApiMessage(error, "Unable to fetch batch details."));
        setDetail(null);
      } finally {
        setLoading(false);
      }
    };

    fetchBatch();
  }, [decodedBatchId, message]);

  if (loading) {
    return (
      <div className="dbiz-root">
        <div className="dbiz-loading">
          <Spin size="large" />
        </div>
      </div>
    );
  }

  const batch = normalizeCouponRecord(detail?.batch || {});
  const batchCoupons = Array.isArray(detail?.coupons) ? detail.coupons.map(normalizeCouponRecord) : [];
  const analytics = detail?.analytics || {};
  const usageDetails = detail?.usageDetails || {};

  if (!detail?.batch) {
    return (
      <div className="dbiz-root">
        <section className="dbiz-card">
          <span className="dbiz-eyebrow">Business</span>
          <h1>Batch not found</h1>
          <p>This coupon batch is unavailable or may have been deleted.</p>
          <div className="dbiz-note" role="button" tabIndex={0} onClick={() => navigate("/dashboard/business/coupons")}>
            Back to coupons
          </div>
        </section>
      </div>
    );
  }

  const totalCoupons = analytics.totalCoupons ?? batch.totalCoupons ?? batch.couponCount ?? batchCoupons.length;
  const activeCoupons = analytics.activeCount ?? batch.activeCount ?? batchCoupons.filter((coupon) => coupon.status === "Active").length;
  const inactiveCoupons = analytics.inactiveCount ?? batch.inactiveCount ?? batchCoupons.filter((coupon) => coupon.status === "InActive").length;
  const totalUsageLimit = analytics.totalUsageLimit ?? batch.totalUsageLimit ?? batch.usageLimit ?? batchCoupons.reduce((sum, coupon) => sum + Number(coupon.usageLimit || 0), 0);
  const totalUsed = analytics.usedCount ?? usageDetails.usedCount ?? batch.usedCount ?? batchCoupons.reduce((sum, coupon) => sum + Number(coupon.usedCount || 0), 0);
  const remainingUsage = analytics.remainingUses ?? usageDetails.remainingUses ?? Math.max(totalUsageLimit - totalUsed, 0);
  const usagePercent = totalUsageLimit ? Math.round((totalUsed / totalUsageLimit) * 100) : 0;

  const batchCouponColumns = [
    { title: "Coupon Code", dataIndex: "couponCode", key: "couponCode", render: (value) => <strong className="dbiz-code">{value}</strong> },
    { title: "Coupon Name", dataIndex: "couponName", key: "couponName" },
    { title: "Service Label", dataIndex: "serviceLabel", key: "serviceLabel" },
    {
      title: "Usage",
      key: "usage",
      render: (_, record) => `${record.usedCount || 0} / ${record.usageLimit || 0}`,
    },
    { title: "Start Date", dataIndex: "startDate", key: "startDate", render: formatDate },
    { title: "Expiry Date", dataIndex: "expiryDate", key: "expiryDate", render: formatDate },
    {
      title: "Status",
      dataIndex: "status",
      key: "status",
      render: (status) => <Tag color={status === "Active" ? "green" : "red"}>{status}</Tag>,
    },
  ];

  return (
    <div className="dbiz-root">
      <div className="dbiz-header dbiz-create-header">
        <div>
          <span className="dbiz-eyebrow">Coupon Batch</span>
          <h1>{batch.batchName || batch.couponName}</h1>
          <p>{batch.couponName} · {batch.serviceLabel}</p>
        </div>
        <Button onClick={() => navigate("/dashboard/business/coupons")}>
          Back to Coupons
        </Button>
      </div>

      <div className="dbiz-summary dbiz-summary-four">
        <div><span>Total Coupons</span><strong>{totalCoupons}</strong></div>
        <div><span>Active</span><strong>{activeCoupons}</strong></div>
        <div><span>Inactive</span><strong>{inactiveCoupons}</strong></div>
        <div><span>Used</span><strong>{totalUsed}</strong></div>
      </div>

      {batch.notesTerms && (
        <div className="dbiz-coupon-notes dbiz-batch-notes">
          <span>Notes / Terms</span>
          <p>{batch.notesTerms}</p>
        </div>
      )}

      <div className="dbiz-batch-analytics">
        <section className="dbiz-analytics-card">
          <div className="dbiz-form-section-head">
            <span>Analytics</span>
            <h2>Batch-level Analytics</h2>
          </div>
          <div className="dbiz-analytics-grid">
            <div><span>Usage Capacity</span><strong>{totalUsageLimit}</strong></div>
            <div><span>Remaining Uses</span><strong>{remainingUsage}</strong></div>
            <div><span>Usage Rate</span><strong>{usagePercent}%</strong></div>
          </div>
          <div className="dbiz-progress">
            <span style={{ width: `${Math.min(usagePercent, 100)}%` }} />
          </div>
        </section>

        <section className="dbiz-analytics-card">
          <div className="dbiz-form-section-head">
            <span>Status</span>
            <h2>Coupon Status Details</h2>
          </div>
          <div className="dbiz-status-split">
            <div><Tag color="green">Active</Tag><strong>{activeCoupons}</strong></div>
            <div><Tag color="red">InActive</Tag><strong>{inactiveCoupons}</strong></div>
          </div>
        </section>
      </div>

      <section className="dbiz-table-card">
        <div className="dbiz-table-head">
          <div>
            <h2>Coupons in this batch</h2>
            <p>Usage details and status for each generated coupon.</p>
          </div>
        </div>
        <Table
          rowKey="id"
          columns={batchCouponColumns}
          dataSource={batchCoupons}
          scroll={{ x: 900 }}
          pagination={{ pageSize: 10 }}
        />
      </section>
    </div>
  );
};

const DynamicQrFormFields = ({
  form,
  updateForm,
  couponOptions,
  couponBatchOptions,
  staffOptions = [],
  canAssignStaff = false,
  editingRecord = null,
}) => {
  const isBatch = form.creationType === "Batch";
  const isBlank = form.qrOption === "Blank";
  const isEditingBatchQr = editingRecord?.rowType === "Batch";
  const canConfigureBlankBatch =
    isEditingBatchQr && (editingRecord?.dynamicQrType === "Blank" || editingRecord?.qrOption === "Blank");
  const instructions = Array.isArray(form.usageInstructions) ? form.usageInstructions : [];
  const extraLinks = Array.isArray(form.extraLinks) ? form.extraLinks : [];
  const selectedLogoName = form.headingLogoFile?.name || "";
  const extraLinkTypeOptions = ["Youtube", "X", "Website", "Facebook", "Twitter", "Other"].map((value) => ({ value, label: value }));

  const ColorField = ({ label, value, onChange }) => (
    <label className="dbiz-color-field">
      {label}
      <div className="dbiz-color-compact">
        <Input type="color" value={value} onChange={(e) => onChange(e.target.value)} />
        <span>{value}</span>
      </div>
    </label>
  );

  const updateInstruction = (index, key, value) => {
    const next = instructions.map((item, itemIndex) =>
      itemIndex === index ? { ...item, [key]: value } : item
    );
    updateForm("usageInstructions", next);
  };

  const addInstruction = () => {
    updateForm("usageInstructions", [...instructions, { title: "", description: "" }]);
  };

  const removeInstruction = (index) => {
    updateForm("usageInstructions", instructions.filter((_, itemIndex) => itemIndex !== index));
  };

  const moveInstruction = (index, direction) => {
    const targetIndex = index + direction;
    if (targetIndex < 0 || targetIndex >= instructions.length) return;
    const next = [...instructions];
    [next[index], next[targetIndex]] = [next[targetIndex], next[index]];
    updateForm("usageInstructions", next);
  };

  const updateExtraLink = (index, key, value) => {
    const next = extraLinks.map((item, itemIndex) =>
      itemIndex === index ? { ...item, [key]: value } : item
    );
    updateForm("extraLinks", next);
  };

  const addExtraLink = () => {
    updateForm("extraLinks", [...extraLinks, { label: "", value: "", type: "Website" }]);
  };

  const removeExtraLink = (index) => {
    updateForm("extraLinks", extraLinks.filter((_, itemIndex) => itemIndex !== index));
  };

  const beforeLogoUpload = (file) => {
    const isAllowedType = ["image/jpeg", "image/png", "image/webp"].includes(file.type);
    const isAllowedSize = file.size / 1024 / 1024 <= 5;
    if (!isAllowedType || !isAllowedSize) return Upload.LIST_IGNORE;
    updateForm("headingLogoFile", file);
    return false;
  };

  return (
    <div className="dbiz-dqr-form">
      <section className="dbiz-dqr-section">
        <div className="dbiz-dqr-section-head">
          <div>
            <span>Step 1</span>
            <h2>QR Setup</h2>
          </div>
          <p>Choose what kind of dynamic QR you want to create.</p>
        </div>
        <div className="dbiz-form-grid">
          <label>
            Dynamic QR Type *
            <Select
              value={form.dynamicQrType}
              disabled={isEditingBatchQr}
              onChange={(value) => updateForm("dynamicQrType", value)}
              options={[{ value: "Master", label: "Master" }, { value: "Blank", label: "Blank" }]}
            />
          </label>
          <label>
            Creation Type *
            <Select
              value={form.creationType}
              disabled={Boolean(editingRecord)}
              onChange={(value) => updateForm("creationType", value)}
              options={[{ value: "Single", label: "Single" }, { value: "Batch", label: "Batch" }]}
            />
          </label>
          <label>
            QR Option *
            <Select
              value={form.qrOption}
              disabled={isEditingBatchQr && !canConfigureBlankBatch}
              onChange={(value) => updateForm("qrOption", value)}
              options={[
                { value: "Link", label: "Link" },
                { value: "CouponMapping", label: "Coupon Mapping" },
                { value: "Blank", label: "Blank" },
              ]}
            />
          </label>
          <label>
            QR Code Name {isBatch && isBlank ? "" : "*"}
            <Input value={form.qrCodeName} onChange={(e) => updateForm("qrCodeName", e.target.value)} placeholder="Summer Campaign" />
          </label>
          <label>
            Expiry Date
            <Input type="date" value={form.expiresAt} onChange={(e) => updateForm("expiresAt", e.target.value)} />
          </label>
          <label>
            Scan Limit
            <Input
              type="number"
              min="0"
              value={form.scanLimit}
              onChange={(e) => updateForm("scanLimit", e.target.value)}
              placeholder="Unlimited"
            />
          </label>
        </div>
        {editingRecord && isBlank && (
          <div className="dbiz-form-help">
            Select Link or Coupon Mapping to configure this blank QR code.
          </div>
        )}
      </section>

      {isBatch && (
        <section className="dbiz-dqr-section">
          <div className="dbiz-dqr-section-head">
            <div>
              <span>Batch</span>
              <h2>Batch Settings</h2>
            </div>
            <p>Generate multiple Dynamic QR codes in one campaign.</p>
          </div>
          <div className="dbiz-form-grid dbiz-form-grid-two">
            <label>
              Batch Name *
              <Input value={form.batchName} onChange={(e) => updateForm("batchName", e.target.value)} placeholder="July QR Batch" />
            </label>
            {!editingRecord && (
              <label>
                Number of QR Codes *
                <Input type="number" min="1" value={form.numberOfQrCodes} onChange={(e) => updateForm("numberOfQrCodes", e.target.value)} placeholder="100" />
              </label>
            )}
          </div>
        </section>
      )}

      {!isBlank && (
        <section className="dbiz-dqr-section">
          <div className="dbiz-dqr-section-head">
            <div>
              <span>Step 2</span>
              <h2>Destination</h2>
            </div>
            <p>Set the link or coupon mapping customers will see after scanning.</p>
          </div>
          <div className="dbiz-form-grid">
            {form.qrOption === "Link" && (
              <label className="dbiz-span-3">
                Link URL *
                <Input value={form.linkUrl} onChange={(e) => updateForm("linkUrl", e.target.value)} placeholder="https://example.com/summer-offer" />
              </label>
            )}
            {form.qrOption === "CouponMapping" && (
              <>
                <label>
                  Coupon Mapping Type *
                  <Select
                    value={form.couponMappingType}
                    onChange={(value) => updateForm("couponMappingType", value)}
                    options={[{ value: "Single", label: "Single Coupons" }, { value: "Batch", label: "Coupon Batch" }]}
                  />
                </label>
                {form.couponMappingType === "Single" ? (
                  <label className="dbiz-span-2">
                    Coupons *
                    <Select
                      mode="multiple"
                      value={form.couponIds}
                      onChange={(value) => updateForm("couponIds", value)}
                      options={couponOptions}
                      placeholder="Select coupons"
                    />
                  </label>
                ) : (
                  <label className="dbiz-span-2">
                    Coupon Batch *
                    <Select
                      value={form.couponBatchId}
                      onChange={(value) => updateForm("couponBatchId", value)}
                      options={couponBatchOptions}
                      placeholder="Select coupon batch"
                    />
                  </label>
                )}
              </>
            )}
          </div>
        </section>
      )}

      {canAssignStaff && (
        <section className="dbiz-dqr-section">
          <div className="dbiz-dqr-section-head">
            <div>
              <span>Access</span>
              <h2>Assign Staff</h2>
            </div>
            <p>Select one or more staff members who can access this Dynamic QR.</p>
          </div>
          <div className="dbiz-form-grid dbiz-form-grid-two">
            <label className="dbiz-span-2">
              Staff Members
              <Select
                mode="multiple"
                allowClear
                value={form.assignedStaffIds}
                onChange={(value) => updateForm("assignedStaffIds", value)}
                options={staffOptions}
                placeholder={staffOptions.length ? "Select staff members" : "No staff users available"}
                disabled={!staffOptions.length}
              />
            </label>
          </div>
        </section>
      )}

      <section className="dbiz-dqr-section">
        <div className="dbiz-dqr-section-head">
          <div>
            <span>Step 3</span>
            <h2>QR Design</h2>
          </div>
          <p>Choose QR size and colors.</p>
        </div>
        <div className="dbiz-form-grid">
          <label>
            Size
            <Select
              value={form.size}
              onChange={(value) => updateForm("size", value)}
              options={[{ value: "Small", label: "Small" }, { value: "Medium", label: "Medium" }, { value: "Large", label: "Large" }]}
            />
          </label>
          <ColorField label="QR Color" value={form.color} onChange={(value) => updateForm("color", value)} />
          <ColorField label="Background Color" value={form.backgroundColor} onChange={(value) => updateForm("backgroundColor", value)} />
        </div>
      </section>

      <section className="dbiz-dqr-section">
        <div className="dbiz-dqr-section-head">
          <div>
            <span>Step 4</span>
            <h2>Landing Page Details</h2>
          </div>
          <p>Optional content shown when customers scan this Dynamic QR.</p>
        </div>
        <div className="dbiz-form-grid">
          <label>
            Heading
            <Input value={form.heading} onChange={(e) => updateForm("heading", e.target.value)} placeholder="Summer Offer" />
          </label>
          <label>
            Subheading
            <Input value={form.subheading} onChange={(e) => updateForm("subheading", e.target.value)} placeholder="Use this coupon at checkout" />
          </label>
          <ColorField label="Card Background" value={form.headingCardBackground} onChange={(value) => updateForm("headingCardBackground", value)} />
          <ColorField label="Heading Text" value={form.headingTextColor} onChange={(value) => updateForm("headingTextColor", value)} />
          <ColorField label="Subheading Text" value={form.subheadingTextColor} onChange={(value) => updateForm("subheadingTextColor", value)} />
          <label className="dbiz-span-3">
            Heading Logo File Upload
            <div className="dbiz-logo-upload">
              {(form.headingLogoUrl || selectedLogoName) && (
                <div className="dbiz-logo-preview">
                  {form.headingLogoUrl && <img src={form.headingLogoUrl} alt="Heading logo" />}
                  <span>{selectedLogoName || "Current logo"}</span>
                </div>
              )}
              <Upload
                accept=".jpg,.jpeg,.png,.webp"
                maxCount={1}
                showUploadList={false}
                beforeUpload={beforeLogoUpload}
              >
                <Button icon={<UploadOutlined />}>Select logo</Button>
              </Upload>
              {form.headingLogoFile && (
                <Button onClick={() => updateForm("headingLogoFile", null)}>Remove selected logo</Button>
              )}
              <small>JPG, JPEG, PNG or WEBP. Max 5 MB.</small>
            </div>
          </label>
          <label className="dbiz-span-3">
            Website Button Link
            <Input value={form.websiteLink} onChange={(e) => updateForm("websiteLink", e.target.value)} placeholder="https://example.com" />
          </label>
          <label className="dbiz-span-3">
            Play Store Link
            <Input value={form.appPlayStoreLink} onChange={(e) => updateForm("appPlayStoreLink", e.target.value)} placeholder="https://play.google.com/store/apps/details?id=..." />
          </label>
          <label className="dbiz-span-3">
            App Store Link
            <Input value={form.appAppleAppStoreLink} onChange={(e) => updateForm("appAppleAppStoreLink", e.target.value)} placeholder="https://apps.apple.com/app/..." />
          </label>
        </div>
      </section>

      <section className="dbiz-dqr-section">
        <div className="dbiz-dqr-section-head">
          <div>
            <span>Step 5</span>
            <h2>Extra Links</h2>
          </div>
          <p>Add helpful links such as videos, social profiles, or a website.</p>
        </div>
        <div className="dbiz-extra-link-list">
          {extraLinks.length === 0 && (
            <div className="dbiz-instruction-empty">No extra links added yet.</div>
          )}
          {extraLinks.map((link, index) => (
            <div className="dbiz-extra-link-row" key={index}>
              <label>
                Label
                <Input value={link.label} onChange={(e) => updateExtraLink(index, "label", e.target.value)} placeholder="Watch this video" />
              </label>
              <label>
                Value / URL
                <Input value={link.value} onChange={(e) => updateExtraLink(index, "value", e.target.value)} placeholder="https://youtube.com/video-id" />
              </label>
              <label>
                Type
                <Select value={link.type} onChange={(value) => updateExtraLink(index, "type", value)} options={extraLinkTypeOptions} />
              </label>
              <Button danger icon={<DeleteOutlined />} onClick={() => removeExtraLink(index)} />
            </div>
          ))}
          <Button className="dbiz-add-instruction-btn" type="dashed" icon={<PlusOutlined />} onClick={addExtraLink}>
            Add link
          </Button>
        </div>
      </section>

      <section className="dbiz-dqr-section">
        <div className="dbiz-dqr-section-head">
          <div>
            <span>Step 6</span>
            <h2>Usage Instructions</h2>
          </div>
          <p>Add plain text steps customers should follow.</p>
        </div>
        <div className="dbiz-instruction-list">
          {instructions.length === 0 && (
            <div className="dbiz-instruction-empty">No instructions added yet.</div>
          )}
          {instructions.map((instruction, index) => (
            <div className="dbiz-instruction-row" key={index}>
              <div className="dbiz-instruction-number">{index + 1}</div>
              <div className="dbiz-instruction-fields">
                <Input
                  value={instruction.title}
                  onChange={(e) => updateInstruction(index, "title", e.target.value)}
                  placeholder="Instruction title"
                />
                <Input.TextArea
                  rows={2}
                  value={instruction.description}
                  onChange={(e) => updateInstruction(index, "description", e.target.value)}
                  placeholder="Instruction description"
                />
              </div>
              <div className="dbiz-instruction-actions">
                <Button size="small" icon={<ArrowUpOutlined />} disabled={index === 0} onClick={() => moveInstruction(index, -1)} />
                <Button size="small" icon={<ArrowDownOutlined />} disabled={index === instructions.length - 1} onClick={() => moveInstruction(index, 1)} />
                <Button size="small" danger icon={<DeleteOutlined />} onClick={() => removeInstruction(index)} />
              </div>
            </div>
          ))}
          <Button className="dbiz-add-instruction-btn" type="dashed" icon={<PlusOutlined />} onClick={addInstruction}>
            Add instruction
          </Button>
        </div>
      </section>
    </div>
  );
};

const DynamicQrPage = () => {
  const { message } = App.useApp();
  const navigate = useNavigate();
  const [rows, setRows] = useState([]);
  const [viewQr, setViewQr] = useState(null);
  const [loading, setLoading] = useState(false);
  const [pagination, setPagination] = useState({ current: 1, pageSize: 20, total: 0 });
  const canView = hasDynamicPermission("dynamic_qr.view");
  const canCreate = hasDynamicPermission("dynamic_qr.create");
  const canUpdate = hasDynamicPermission("dynamic_qr.update");
  const canDelete = hasDynamicPermission("dynamic_qr.delete");
  const canViewAnalytics = hasDynamicPermission("dynamic_qr.analytics.view");

  const fetchDynamicQr = async (page = pagination.current, pageSize = pagination.pageSize) => {
    setLoading(true);
    try {
      const response = await axios.get(`${apiBase}/api/DynamicQr`, {
        headers: authHeaders(),
        params: { page, pageSize },
      });
      const result = response.data?.data || {};
      const data = Array.isArray(result.data) ? result.data : [];
      setRows(data.map(normalizeDynamicQrRecord));
      setPagination({
        current: result.page || page,
        pageSize: result.pageSize || pageSize,
        total: result.total || data.length,
      });
    } catch (error) {
      message.error(getApiMessage(error, "Unable to fetch dynamic QR codes."));
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    if (!canView) return;
    fetchDynamicQr(1, pagination.pageSize);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [canView]);

  const fetchQrDetail = async (record) => {
    try {
      const response = await axios.get(`${apiBase}/api/DynamicQr/${record.id}`, {
        headers: authHeaders(),
      });
      const data = response.data?.data || {};
      return normalizeDynamicQrRecord(data.qrCode || data.dynamicQr || data || record);
    } catch {
      return record;
    }
  };

  const handleViewQr = async (record) => {
    if (record.rowType === "Batch") {
      navigate(`/dashboard/business/dynamic-qr/batch/${encodeURIComponent(record.batchId)}`);
      return;
    }
    setViewQr(await fetchQrDetail(record));
  };

  const handleEditQr = (record) => {
    if (!canUpdate) {
      message.warning("You do not have permission to update Dynamic QR codes.");
      return;
    }
    if (record.rowType === "Batch") {
      navigate(`/dashboard/business/dynamic-qr/batch/${encodeURIComponent(record.batchId)}/edit`);
      return;
    }
    navigate(`/dashboard/business/dynamic-qr/${encodeURIComponent(record.id)}/edit`);
  };

  const toggleDynamicQrStatus = async (record) => {
    if (!canUpdate) {
      message.warning("You do not have permission to update Dynamic QR codes.");
      return;
    }
    const action = record.status === "Active" || record.status === "Mixed" ? "deactivate" : "activate";
    const endpoint = record.rowType === "Batch"
      ? `${apiBase}/api/DynamicQr/batches/${record.batchId}/${action}`
      : `${apiBase}/api/DynamicQr/${record.id}/${action}`;

    try {
      await axios.post(endpoint, {}, { headers: authHeaders() });
      message.success(record.rowType === "Batch" ? "Dynamic QR batch status updated." : "Dynamic QR status updated.");
      fetchDynamicQr(pagination.current, pagination.pageSize);
    } catch (error) {
      message.error(getApiMessage(error, "Unable to update status."));
    }
  };

  const deleteDynamicQr = async (record) => {
    if (!canDelete) {
      message.warning("You do not have permission to delete Dynamic QR codes.");
      return;
    }
    const endpoint = record.rowType === "Batch"
      ? `${apiBase}/api/DynamicQr/batches/${record.batchId}`
      : `${apiBase}/api/DynamicQr/${record.id}`;

    try {
      await axios.delete(endpoint, { headers: authHeaders() });
      message.success(record.rowType === "Batch" ? "Dynamic QR batch deleted." : "Dynamic QR deleted.");
      fetchDynamicQr(pagination.current, pagination.pageSize);
    } catch (error) {
      message.error(getApiMessage(error, "Unable to delete dynamic QR."));
    }
  };

  const downloadQr = async (record) => {
    try {
      await downloadAuthenticatedFile(`${apiBase}/api/DynamicQr/${record.id}/download?format=png`, {
        token: localStorage.getItem("token") || "",
        fileName: safeFileName(record.qrCodeName || "dynamic-qr", "png"),
      });
    } catch (error) {
      message.error(getApiMessage(error, "Unable to download dynamic QR."));
    }
  };

  const exportBatch = async (record) => {
    try {
      await downloadAuthenticatedFile(`${apiBase}/api/DynamicQr/batches/${record.batchId}/export`, {
        token: localStorage.getItem("token") || "",
        fileName: safeFileName(record.batchName || "dynamic-qr-batch", "csv"),
      });
    } catch (error) {
      message.error(getApiMessage(error, "Unable to export dynamic QR batch."));
    }
  };

  const columns = [
    {
      title: "Name",
      dataIndex: "qrCodeName",
      key: "qrCodeName",
      render: (text, record) => (
        <div>
          <strong className="dbiz-table-title">{record.rowType === "Batch" ? record.batchName || text : text}</strong>
          <small className="dbiz-table-sub">
            {record.dynamicQrType} · {record.qrOption}
          </small>
        </div>
      ),
    },
    {
      title: "Type",
      key: "type",
      render: (_, record) => <Tag color={record.rowType === "Batch" ? "purple" : "blue"}>{record.rowType}</Tag>,
    },
    {
      title: "QR Info",
      key: "qrInfo",
      render: (_, record) => record.rowType === "Batch" ? `${record.qrCount} QR codes` : record.linkUrl || record.qrOption,
    },
    { title: "Scans", dataIndex: "scanCount", key: "scanCount" },
    { title: "Scan Limit", dataIndex: "scanLimit", key: "scanLimit", render: formatScanLimit },
    { title: "Remaining", dataIndex: "remainingScans", key: "remainingScans", render: formatRemainingScans },
    { title: "Expires", dataIndex: "expiresAt", key: "expiresAt", render: formatDate },
    {
      title: "Status",
      dataIndex: "status",
      key: "status",
      render: (status) => <Tag color={status === "Active" ? "green" : status === "Mixed" ? "gold" : "red"}>{status}</Tag>,
    },
    {
      title: "Actions",
      key: "actions",
      fixed: "right",
      render: (_, record) => (
        <div className="dbiz-actions" onClick={(event) => event.stopPropagation()}>
          <button type="button" className="view" title="View" onClick={() => handleViewQr(record)}><EyeOutlined /></button>
          {canUpdate && <button type="button" className="edit" title="Edit" onClick={() => handleEditQr(record)}><EditOutlined /></button>}
          <button
            type="button"
            className="download"
            title={record.rowType === "Batch" ? "Export CSV" : "Download QR"}
            onClick={() => record.rowType === "Batch" ? exportBatch(record) : downloadQr(record)}
          >
            <DownloadOutlined />
          </button>
          {canUpdate && <button type="button" className={record.status === "Active" ? "danger" : "success"} title={record.status === "Active" ? "Deactivate" : "Activate"} onClick={() => toggleDynamicQrStatus(record)}><PoweroffOutlined /></button>}
          {canDelete && (
            <Popconfirm title={record.rowType === "Batch" ? "Delete this batch?" : "Delete this QR?"} onConfirm={() => deleteDynamicQr(record)}>
              <button type="button" className="delete" title="Delete"><DeleteOutlined /></button>
            </Popconfirm>
          )}
        </div>
      ),
    },
  ];

  if (!canView) {
    return (
      <div className="dbiz-root">
        <section className="dbiz-card">
          <span className="dbiz-eyebrow">Dynamic QR</span>
          <h1>Access restricted</h1>
          <p>You do not have permission to view Dynamic QR codes.</p>
        </section>
      </div>
    );
  }

  return (
    <div className="dbiz-root">
      <div className="dbiz-header">
        <div>
          <span className="dbiz-eyebrow">Business</span>
          <h1>Dynamic QR Code</h1>
          <p>Create editable QR links, blank QR codes, and coupon-mapped QR campaigns.</p>
        </div>
        {canCreate && (
          <Button type="primary" icon={<PlusOutlined />} onClick={() => navigate("/dashboard/business/dynamic-qr/new")}>
            Add Dynamic QR
          </Button>
        )}
      </div>

      {canViewAnalytics && (
        <div className="dbiz-summary">
          <div><span>Total Records</span><strong>{pagination.total}</strong></div>
          <div><span>Active</span><strong>{rows.filter((row) => row.status === "Active").length}</strong></div>
          <div><span>Scans</span><strong>{rows.reduce((sum, row) => sum + Number(row.scanCount || 0), 0)}</strong></div>
        </div>
      )}

      <div className="dbiz-table-card">
        <Table
          rowKey="id"
          columns={columns}
          dataSource={rows}
          loading={loading}
          scroll={{ x: 1300 }}
          pagination={pagination}
          onChange={(nextPagination) => fetchDynamicQr(nextPagination.current, nextPagination.pageSize)}
          locale={{ emptyText: "No dynamic QR codes created yet." }}
          rowClassName={(record) => record.rowType === "Batch" ? "dbiz-clickable-row" : ""}
          onRow={(record) => ({
            onClick: () => {
              if (record.rowType === "Batch") {
                navigate(`/dashboard/business/dynamic-qr/batch/${encodeURIComponent(record.batchId)}`);
              }
            },
          })}
        />
      </div>

      <Modal open={Boolean(viewQr)} onCancel={() => setViewQr(null)} footer={null} title="Dynamic QR Details" width={760} centered>
        {viewQr && (
          <div className="dbiz-qr-detail-modal">
            <div className="dbiz-qr-preview">
              {viewQr.qrImageUrl || viewQr.qrSvgUrl ? (
                <img src={viewQr.qrImageUrl || viewQr.qrSvgUrl} alt={viewQr.qrCodeName || "Dynamic QR"} />
              ) : (
                <div className="dbiz-qr-fallback">QR</div>
              )}
            </div>
            <div className="dbiz-qr-detail-content">
              <div className="dbiz-detail">
                {[
                  ["QR Code Name", viewQr.qrCodeName || "-"],
                  ["Dynamic QR Type", viewQr.dynamicQrType],
                  ["QR Option", viewQr.qrOption],
                  ["Link URL", viewQr.linkUrl || "-"],
                  ["QR URL", viewQr.qrUrl || viewQr.publicUrl || viewQr.scanUrl || "-"],
                  ["Scan Count", viewQr.scanCount || 0],
                  ["Scan Limit", formatScanLimit(viewQr.scanLimit)],
                  ["Remaining Scans", formatRemainingScans(viewQr.remainingScans)],
                  ["Limit Reached", viewQr.isScanLimitReached ? "Yes" : "No"],
                  ["Applied Count", viewQr.couponAppliedCount || 0],
                  ["Copied Count", viewQr.couponCopiedCount || 0],
                  ["Expires At", formatDate(viewQr.expiresAt)],
                  ["Status", viewQr.status],
                ].map(([label, value]) => (
                  <div key={label}><span>{label}</span><strong>{value}</strong></div>
                ))}
              </div>
              <div className="dbiz-qr-result-actions">
                <Button type="primary" icon={<DownloadOutlined />} onClick={() => downloadQr(viewQr)}>
                  Download QR
                </Button>
                {(viewQr.qrUrl || viewQr.publicUrl || viewQr.scanUrl) && (
                  <Button onClick={() => window.open(viewQr.qrUrl || viewQr.publicUrl || viewQr.scanUrl, "_blank", "noopener,noreferrer")}>
                    Open QR Link
                  </Button>
                )}
              </div>
            </div>
          </div>
        )}
      </Modal>
    </div>
  );
};

const DynamicQrCreatePage = () => {
  const { message } = App.useApp();
  const navigate = useNavigate();
  const { qrId = "", batchId = "" } = useParams();
  const decodedQrId = decodeURIComponent(qrId);
  const decodedBatchId = decodeURIComponent(batchId);
  const isEditMode = Boolean(decodedQrId || decodedBatchId);
  const isBatchEditMode = Boolean(decodedBatchId);
  const [form, setForm] = useState(initialDynamicQrForm);
  const [couponOptions, setCouponOptions] = useState([]);
  const [couponBatchOptions, setCouponBatchOptions] = useState([]);
  const [staffOptions, setStaffOptions] = useState([]);
  const [staffRecords, setStaffRecords] = useState([]);
  const [createdQr, setCreatedQr] = useState(null);
  const [editingRecord, setEditingRecord] = useState(null);
  const [loadingDetail, setLoadingDetail] = useState(false);
  const [saving, setSaving] = useState(false);

  const isBatch = form.creationType === "Batch";
  const isBlank = form.qrOption === "Blank";
  const canCreate = hasDynamicPermission("dynamic_qr.create");
  const canUpdate = hasDynamicPermission("dynamic_qr.update");
  const canAssignStaff = hasDynamicPermission("dynamic_qr.assign_staff");
  const canUsePage = isEditMode ? canUpdate : canCreate;

  const updateForm = (key, value) => {
    setForm((prev) => {
      const next = { ...prev, [key]: value };
      if (key === "dynamicQrType" && value === "Blank") {
        next.qrOption = "Blank";
        next.linkUrl = "";
        next.couponIds = [];
        next.couponBatchId = "";
      }
      if (key === "dynamicQrType" && value === "Master" && prev.qrOption === "Blank") {
        next.qrOption = "Link";
      }
      if (key === "qrOption" && value === "Blank") {
        next.dynamicQrType = "Blank";
        next.linkUrl = "";
        next.couponIds = [];
        next.couponBatchId = "";
      }
      if (key === "qrOption" && value !== "Blank" && prev.dynamicQrType === "Blank") {
        next.dynamicQrType = "Master";
      }
      if (key === "qrOption" && value === "Link") {
        next.couponIds = [];
        next.couponBatchId = "";
      }
      if (key === "qrOption" && value === "CouponMapping") {
        next.linkUrl = "";
      }
      if (key === "couponMappingType" && value === "Single") {
        next.couponBatchId = "";
      }
      if (key === "couponMappingType" && value === "Batch") {
        next.couponIds = [];
      }
      return next;
    });
  };

  const fetchCouponMappingOptions = async () => {
    try {
      const response = await axios.get(`${apiBase}/api/Coupons`, {
        headers: authHeaders(),
        params: { page: 1, pageSize: 200 },
      });
      const data = response.data?.data?.data || [];
      const normalized = data.map(normalizeCouponRecord);
      setCouponOptions(
        normalized
          .filter((coupon) => coupon.rowType !== "Batch")
          .map((coupon) => ({
            value: toSelectValue(coupon.id),
            label: `${coupon.couponCode} - ${coupon.couponName}`,
          }))
      );
      setCouponBatchOptions(
        normalized
          .filter((coupon) => coupon.rowType === "Batch")
          .map((batch) => ({
            value: toSelectValue(batch.batchId),
            label: `${batch.batchName || batch.couponName} (${batch.couponCount} coupons)`,
          }))
      );
    } catch {
      setCouponOptions([]);
      setCouponBatchOptions([]);
    }
  };

  const fetchStaffOptions = async () => {
    try {
      const response = await axios.get(`${apiBase}/api/DynamicQr/access/staff`, {
        headers: authHeaders(),
      });
      const records = unwrapArray(response.data);
      setStaffRecords(records);
      setStaffOptions(
        records.map((staff) => {
          const id = staff.id ?? staff.staffId;
          const name = `${staff.firstName || ""} ${staff.lastName || ""}`.trim();
          return {
            value: normalizeId(id),
            label: `${name || staff.displayName || staff.email || "Staff"}${staff.email ? ` - ${staff.email}` : ""}`,
          };
        }).filter((item) => item.value)
      );
      return records;
    } catch {
      setStaffRecords([]);
      setStaffOptions([]);
      return [];
    }
  };

  useEffect(() => {
    if (!canUsePage) return;
    const fetchEditDetail = async (availableStaff = []) => {
      if (!isEditMode) return;

      setLoadingDetail(true);
      try {
        const endpoint = isBatchEditMode
          ? `${apiBase}/api/DynamicQr/batches/${decodedBatchId}`
          : `${apiBase}/api/DynamicQr/${decodedQrId}`;
        const response = await axios.get(endpoint, { headers: authHeaders() });
        const data = response.data?.data || {};
        const firstBatchQr = isBatchEditMode && Array.isArray(data.qrCodes) ? data.qrCodes[0] || {} : {};
        const batchRecord = data.batch || data.dynamicQrBatch || data;
        const rawRecord = isBatchEditMode
          ? {
              ...firstBatchQr,
              ...batchRecord,
              qrCodeName: batchRecord?.qrCodeName || firstBatchQr.qrCodeName || "",
              linkUrl: batchRecord?.linkUrl ?? firstBatchQr.linkUrl ?? "",
              couponMappingType: batchRecord?.couponMappingType || firstBatchQr.couponMappingType || "",
              mappedCouponBatchId: batchRecord?.mappedCouponBatchId ?? firstBatchQr.mappedCouponBatchId,
              couponBatchId: batchRecord?.couponBatchId ?? firstBatchQr.couponBatchId,
              couponMappings: batchRecord?.couponMappings || firstBatchQr.couponMappings || [],
              coupons: batchRecord?.coupons || firstBatchQr.coupons || [],
              expiresAt: batchRecord?.expiresAt || firstBatchQr.expiresAt || "",
              scanLimit: firstBatchQr.scanLimit ?? batchRecord?.perQrScanLimit ?? batchRecord?.scanLimit ?? null,
            }
          : data.qrCode || data.dynamicQr || data;
        const detail = normalizeDynamicQrRecord({
          ...rawRecord,
          rowType: isBatchEditMode ? "Batch" : rawRecord.rowType,
          batchId: isBatchEditMode ? decodedBatchId : rawRecord.batchId,
        });

        setEditingRecord(detail);
        const qrIdsInRecord = extractDynamicQrIds(isBatchEditMode ? { qrCodes: data.qrCodes || [] } : detail);
        const assignedStaffIds = canAssignStaff
          ? availableStaff
              .filter((staff) => {
                const assignedQrIds = extractAssignedQrIdsFromStaff(staff);
                return assignedQrIds.some((qrIdValue) => qrIdsInRecord.includes(qrIdValue));
              })
              .map((staff) => normalizeId(staff.id ?? staff.staffId))
              .filter(Boolean)
          : [];

        setForm({
          ...getDynamicQrFormFromRecord(detail),
          assignedStaffIds,
        });
      } catch (error) {
        message.error(getApiMessage(error, "Unable to load dynamic QR for editing."));
        navigate("/dashboard/business/dynamic-qr");
      } finally {
        setLoadingDetail(false);
      }
    };

    fetchCouponMappingOptions();
    if (canAssignStaff) {
      fetchStaffOptions().then((records) => fetchEditDetail(records));
    } else {
      fetchEditDetail();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [decodedQrId, decodedBatchId, isEditMode, isBatchEditMode, canUsePage, canAssignStaff]);

  const validateDynamicQr = () => {
    if (!form.dynamicQrType) return "Dynamic QR Type is mandatory.";
    if (!form.creationType) return "Creation Type is mandatory.";
    if (isBlank && !isBatch && !form.qrCodeName.trim()) return "QR Code Name is mandatory.";
    if (!isBlank && !form.qrCodeName.trim()) return "QR Code Name is mandatory.";
    if (!form.qrOption) return "QR Option is mandatory.";
    if (form.expiresAt && form.expiresAt < new Date().toISOString().slice(0, 10)) {
      return "Expiry date cannot be a past date.";
    }
    if (form.scanLimit !== "" && Number(form.scanLimit) < 0) {
      return "Scan Limit cannot be negative.";
    }
    const extraLinkError = validateExtraLinks(form.extraLinks);
    if (extraLinkError) return extraLinkError;
    const websiteLinkError = validateOptionalUrl(form.websiteLink, "Website Button Link");
    if (websiteLinkError) return websiteLinkError;
    const playStoreLinkError = validateOptionalUrl(form.appPlayStoreLink, "Play Store Link");
    if (playStoreLinkError) return playStoreLinkError;
    const appStoreLinkError = validateOptionalUrl(form.appAppleAppStoreLink, "App Store Link");
    if (appStoreLinkError) return appStoreLinkError;
    if (form.qrOption === "Link" && !form.linkUrl.trim()) return "Link URL is mandatory.";
    if (form.qrOption === "CouponMapping") {
      if (form.couponMappingType === "Batch" && !form.couponBatchId) return "Coupon batch is mandatory.";
      if (form.couponMappingType === "Single" && form.couponIds.length === 0) return "Select at least one coupon.";
    }
    if (isBatch) {
      if (!form.batchName.trim()) return "Batch Name is mandatory.";
      if (!isEditMode && (!Number(form.numberOfQrCodes) || Number(form.numberOfQrCodes) < 1)) {
        return "Number of QR Codes is mandatory.";
      }
    }
    return "";
  };

  const areSameIds = (first = [], second = []) => {
    const normalizeList = (items) => items.map(normalizeId).filter(Boolean).sort();
    return JSON.stringify(normalizeList(first)) === JSON.stringify(normalizeList(second));
  };

  const getQrIdsForAssignment = async (result = {}) => {
    let qrIds = extractDynamicQrIds(result);
    const batchIdForLookup = result.batchId || result.batch?.batchId || decodedBatchId;

    if (qrIds.length === 0 && (form.creationType === "Batch" || batchIdForLookup)) {
      try {
        const response = await axios.get(`${apiBase}/api/DynamicQr/batches/${batchIdForLookup}`, {
          headers: authHeaders(),
        });
        qrIds = extractDynamicQrIds({ qrCodes: response.data?.data?.qrCodes || [] });
      } catch {
        qrIds = [];
      }
    }

    if (qrIds.length === 0 && decodedQrId) {
      qrIds = [normalizeId(decodedQrId)];
    }

    return qrIds;
  };

  const syncStaffAssignments = async (qrIds = []) => {
    if (!canAssignStaff) return;
    const normalizedQrIds = qrIds.map(normalizeId).filter(Boolean);
    if (normalizedQrIds.length === 0) return;

    const selectedStaffIds = (form.assignedStaffIds || []).map(normalizeId).filter(Boolean);
    const targetStaffIds = isEditMode
      ? staffRecords.map((staff) => normalizeId(staff.id ?? staff.staffId)).filter(Boolean)
      : selectedStaffIds;

    const toApiQrId = (id) => (/^\d+$/.test(id) ? Number(id) : id);

    await Promise.all(targetStaffIds.map(async (staffId) => {
      const staff = staffRecords.find((item) => normalizeId(item.id ?? item.staffId) === staffId) || {};
      const existingQrIds = extractAssignedQrIdsFromStaff(staff);
      const shouldAssign = selectedStaffIds.includes(staffId);
      const nextQrIds = shouldAssign
        ? [...new Set([...existingQrIds, ...normalizedQrIds])]
        : existingQrIds.filter((id) => !normalizedQrIds.includes(id));

      if (areSameIds(existingQrIds, nextQrIds)) return;

      await axios.post(
        `${apiBase}/api/DynamicQr/access/staff/${staffId}/assignments`,
        { qrCodeIds: nextQrIds.map(toApiQrId) },
        { headers: authHeaders() }
      );
    }));
  };

  const syncAssignmentsFromResponse = async (result = {}) => {
    try {
      const qrIds = await getQrIdsForAssignment(result);
      await syncStaffAssignments(qrIds);
    } catch (error) {
      message.warning(getApiMessage(error, "Dynamic QR saved, but staff assignment could not be updated."));
    }
  };

  const handleCreateDynamicQr = async () => {
    if (!canUsePage) {
      message.warning(isEditMode ? "You do not have permission to update Dynamic QR codes." : "You do not have permission to create Dynamic QR codes.");
      return;
    }
    const validationError = validateDynamicQr();
    if (validationError) {
      message.warning(validationError);
      return;
    }

    setSaving(true);
    try {
      if (isBatchEditMode) {
        const request = getDynamicQrRequestBody(getDynamicQrBatchUpdatePayload(form), form);
        const response = await axios.put(`${apiBase}/api/DynamicQr/batches/${decodedBatchId}`, request.body, { headers: request.headers });
        await syncAssignmentsFromResponse(response.data?.data || { batchId: decodedBatchId });
        message.success("Dynamic QR batch updated.");
        navigate("/dashboard/business/dynamic-qr");
      } else if (isEditMode) {
        const request = getDynamicQrRequestBody(getDynamicQrUpdatePayload(form), form);
        const response = await axios.put(`${apiBase}/api/DynamicQr/${decodedQrId}`, request.body, { headers: request.headers });
        await syncAssignmentsFromResponse(response.data?.data || { id: decodedQrId });
        message.success("Dynamic QR updated.");
        navigate("/dashboard/business/dynamic-qr");
      } else {
        const request = getDynamicQrRequestBody(getDynamicQrPayload(form), form);
        const response = await axios.post(`${apiBase}/api/DynamicQr`, request.body, { headers: request.headers });
        const created = normalizeDynamicQrRecord(response.data?.data || {});
        await syncAssignmentsFromResponse(response.data?.data || created);
        setCreatedQr(created);
        message.success(isBatch ? "Dynamic QR batch created." : "Dynamic QR created.");
      }
    } catch (error) {
      message.error(getApiMessage(error, isEditMode ? "Unable to update dynamic QR." : "Unable to create dynamic QR."));
    } finally {
      setSaving(false);
    }
  };

  const downloadQr = async (record) => {
    try {
      await downloadAuthenticatedFile(`${apiBase}/api/DynamicQr/${record.id}/download?format=png`, {
        token: localStorage.getItem("token") || "",
        fileName: safeFileName(record.qrCodeName || "dynamic-qr", "png"),
      });
    } catch (error) {
      message.error(getApiMessage(error, "Unable to download dynamic QR."));
    }
  };

  const exportBatch = async (record) => {
    try {
      await downloadAuthenticatedFile(`${apiBase}/api/DynamicQr/batches/${record.batchId}/export`, {
        token: localStorage.getItem("token") || "",
        fileName: safeFileName(record.batchName || "dynamic-qr-batch", "csv"),
      });
    } catch (error) {
      message.error(getApiMessage(error, "Unable to export dynamic QR batch."));
    }
  };

  const resetCreateForm = () => {
    setForm(initialDynamicQrForm);
    setCreatedQr(null);
  };

  if (loadingDetail) {
    return (
      <div className="dbiz-root">
        <div className="dbiz-loading"><Spin size="large" /></div>
      </div>
    );
  }

  if (!canUsePage) {
    return (
      <div className="dbiz-root">
        <section className="dbiz-card">
          <span className="dbiz-eyebrow">Dynamic QR</span>
          <h1>Access restricted</h1>
          <p>{isEditMode ? "You do not have permission to update Dynamic QR codes." : "You do not have permission to create Dynamic QR codes."}</p>
          <div className="dbiz-note" role="button" tabIndex={0} onClick={() => navigate("/dashboard/business/dynamic-qr")}>
            Back to Dynamic QR
          </div>
        </section>
      </div>
    );
  }

  return (
    <div className="dbiz-root">
      <div className="dbiz-header">
        <div>
          <span className="dbiz-eyebrow">Business</span>
          <h1>{isEditMode ? "Edit Dynamic QR" : "Add Dynamic QR"}</h1>
          <p>
            {isEditMode
              ? "Update QR destination, coupon mapping, landing details, and QR styling."
              : "Create a single QR, batch QR, blank QR, link QR, or coupon-mapped QR campaign."}
          </p>
        </div>
        <Button onClick={() => navigate("/dashboard/business/dynamic-qr")}>Back to Dynamic QR</Button>
      </div>

      {!isEditMode && createdQr?.id && (
        <section className="dbiz-qr-result">
          <div className="dbiz-qr-preview">
            {createdQr.rowType === "Batch" ? (
              <div className="dbiz-qr-batch-preview">
                <strong>{createdQr.qrCount || createdQr.totalQrCodes || form.numberOfQrCodes || 0}</strong>
                <span>QR Codes</span>
              </div>
            ) : createdQr.qrImageUrl || createdQr.qrSvgUrl ? (
              <img src={createdQr.qrImageUrl || createdQr.qrSvgUrl} alt={createdQr.qrCodeName || "Dynamic QR"} />
            ) : (
              <div className="dbiz-qr-fallback">QR</div>
            )}
          </div>
          <div className="dbiz-qr-result-body">
            <span className="dbiz-eyebrow">Generated</span>
            <h2>{createdQr.displayName || createdQr.qrCodeName || createdQr.batchName || "Dynamic QR Code"}</h2>
            <p>{createdQr.rowType === "Batch" ? "Batch QR codes generated successfully." : "Your dynamic QR code is ready to use."}</p>
            {(createdQr.qrUrl || createdQr.publicUrl || createdQr.scanUrl) && (
              <div className="dbiz-qr-url">{createdQr.qrUrl || createdQr.publicUrl || createdQr.scanUrl}</div>
            )}
            <div className="dbiz-qr-result-actions">
              {createdQr.rowType === "Batch" ? (
                <>
                  <Button icon={<EyeOutlined />} onClick={() => navigate(`/dashboard/business/dynamic-qr/batch/${encodeURIComponent(createdQr.batchId)}`)}>
                    View Batch
                  </Button>
                  <Button type="primary" icon={<DownloadOutlined />} onClick={() => exportBatch(createdQr)}>
                    Export CSV
                  </Button>
                </>
              ) : (
                <>
                  {(createdQr.qrUrl || createdQr.publicUrl || createdQr.scanUrl) && (
                    <Button icon={<EyeOutlined />} onClick={() => window.open(createdQr.qrUrl || createdQr.publicUrl || createdQr.scanUrl, "_blank", "noopener,noreferrer")}>
                      Open QR Link
                    </Button>
                  )}
                  <Button type="primary" icon={<DownloadOutlined />} onClick={() => downloadQr(createdQr)}>
                    Download QR
                  </Button>
                </>
              )}
              <Button onClick={resetCreateForm}>Create Another</Button>
            </div>
          </div>
        </section>
      )}

      <section className="dbiz-create-card">
        <div className="dbiz-form-section-head">
          <span>Dynamic QR</span>
          <h2>QR setup</h2>
        </div>
        <DynamicQrFormFields
          form={form}
          updateForm={updateForm}
          couponOptions={couponOptions}
          couponBatchOptions={couponBatchOptions}
          staffOptions={staffOptions}
          canAssignStaff={canAssignStaff}
          editingRecord={editingRecord}
        />
        <div className="dbiz-create-actions">
          <Button onClick={() => navigate("/dashboard/business/dynamic-qr")}>Cancel</Button>
          <Button type="primary" loading={saving} onClick={handleCreateDynamicQr}>
            {isEditMode ? "Update Dynamic QR" : "Create Dynamic QR"}
          </Button>
        </div>
      </section>
    </div>
  );
};

const DynamicQrBatchDetailPage = () => {
  const { message } = App.useApp();
  const navigate = useNavigate();
  const { batchId = "" } = useParams();
  const decodedBatchId = decodeURIComponent(batchId);
  const [loading, setLoading] = useState(true);
  const [detail, setDetail] = useState(null);
  const canUpdate = hasDynamicPermission("dynamic_qr.update");
  const canViewAnalytics = hasDynamicPermission("dynamic_qr.analytics.view");

  useEffect(() => {
    const fetchBatch = async () => {
      setLoading(true);
      try {
        const response = await axios.get(`${apiBase}/api/DynamicQr/batches/${decodedBatchId}`, {
          headers: authHeaders(),
        });
        setDetail(response.data?.data || null);
      } catch (error) {
        message.error(getApiMessage(error, "Unable to fetch dynamic QR batch."));
        setDetail(null);
      } finally {
        setLoading(false);
      }
    };

    fetchBatch();
  }, [decodedBatchId, message]);

  if (loading) {
    return (
      <div className="dbiz-root">
        <div className="dbiz-loading"><Spin size="large" /></div>
      </div>
    );
  }

  const batch = normalizeDynamicQrRecord(detail?.batch || {});
  const analytics = detail?.analytics || {};
  const qrCodes = Array.isArray(detail?.qrCodes) ? detail.qrCodes.map(normalizeDynamicQrRecord) : [];
  const totalQrCodes = analytics.totalQrCodes ?? batch.qrCount ?? qrCodes.length;
  const activeCount = analytics.activeCount ?? qrCodes.filter((qr) => qr.status === "Active").length;
  const inactiveCount = analytics.inactiveCount ?? qrCodes.filter((qr) => qr.status === "InActive").length;
  const scanCount = analytics.scanCount ?? qrCodes.reduce((sum, qr) => sum + Number(qr.scanCount || 0), 0);
  const scanLimit = analytics.scanLimit ?? batch.scanLimit ?? null;
  const remainingScans = analytics.remainingScans ?? batch.remainingScans ?? null;
  const isScanLimitReached = Boolean(analytics.isScanLimitReached ?? batch.isScanLimitReached);

  const downloadBatchQr = async (record) => {
    try {
      await downloadAuthenticatedFile(`${apiBase}/api/DynamicQr/${record.id}/download?format=png`, {
        token: localStorage.getItem("token") || "",
        fileName: safeFileName(record.qrCodeName || "dynamic-qr", "png"),
      });
    } catch (error) {
      message.error(getApiMessage(error, "Unable to download dynamic QR."));
    }
  };

  if (!detail?.batch) {
    return (
      <div className="dbiz-root">
        <section className="dbiz-card">
          <span className="dbiz-eyebrow">Business</span>
          <h1>Batch not found</h1>
          <p>This dynamic QR batch is unavailable or may have been deleted.</p>
          <div className="dbiz-note" role="button" tabIndex={0} onClick={() => navigate("/dashboard/business/dynamic-qr")}>
            Back to dynamic QR
          </div>
        </section>
      </div>
    );
  }

  const columns = [
    {
      title: "QR Code",
      key: "qrPreview",
      render: (_, record) => (
        <div className="dbiz-batch-qr-cell">
          {record.qrImageUrl || record.qrSvgUrl ? (
            <img src={record.qrImageUrl || record.qrSvgUrl} alt={record.qrCodeName || "Dynamic QR"} />
          ) : (
            <span>QR</span>
          )}
        </div>
      ),
    },
    { title: "QR Code Name", dataIndex: "qrCodeName", key: "qrCodeName" },
    { title: "Option", dataIndex: "qrOption", key: "qrOption" },
    { title: "Scans", dataIndex: "scanCount", key: "scanCount" },
    { title: "Scan Limit", dataIndex: "scanLimit", key: "scanLimit", render: formatScanLimit },
    { title: "Remaining", dataIndex: "remainingScans", key: "remainingScans", render: formatRemainingScans },
    { title: "Limit Reached", dataIndex: "isScanLimitReached", key: "isScanLimitReached", render: (value) => value ? "Yes" : "No" },
    { title: "Expires", dataIndex: "expiresAt", key: "expiresAt", render: formatDate },
    { title: "Status", dataIndex: "status", key: "status", render: (status) => <Tag color={status === "Active" ? "green" : "red"}>{status}</Tag> },
    {
      title: "Actions",
      key: "actions",
      render: (_, record) => (
        <div className="dbiz-actions">
          <button type="button" className="download" title="Download QR" onClick={() => downloadBatchQr(record)}><DownloadOutlined /></button>
          {(record.qrUrl || record.publicUrl || record.scanUrl) && (
            <button
              type="button"
              className="view"
              title="Open QR Link"
              onClick={() => window.open(record.qrUrl || record.publicUrl || record.scanUrl, "_blank", "noopener,noreferrer")}
            >
              <EyeOutlined />
            </button>
          )}
        </div>
      ),
    },
  ];

  return (
    <div className="dbiz-root">
      <div className="dbiz-header dbiz-create-header">
        <div>
          <span className="dbiz-eyebrow">Dynamic QR Batch</span>
          <h1>{batch.batchName || batch.qrCodeName}</h1>
          <p>{batch.dynamicQrType} · {batch.qrOption}</p>
        </div>
        <div className="dbiz-header-actions">
          {canUpdate && (
            <Button icon={<EditOutlined />} onClick={() => navigate(`/dashboard/business/dynamic-qr/batch/${encodeURIComponent(decodedBatchId)}/edit`)}>
              Edit Batch
            </Button>
          )}
          <Button onClick={() => navigate("/dashboard/business/dynamic-qr")}>Back to Dynamic QR</Button>
        </div>
      </div>

      {canViewAnalytics && (
        <>
          <div className="dbiz-summary dbiz-summary-six">
            <div><span>Total QR Codes</span><strong>{totalQrCodes}</strong></div>
            <div><span>Active</span><strong>{activeCount}</strong></div>
            <div><span>Inactive</span><strong>{inactiveCount}</strong></div>
            <div><span>Scans</span><strong>{scanCount}</strong></div>
            <div><span>Scan Limit</span><strong>{formatScanLimit(scanLimit)}</strong></div>
            <div><span>Remaining</span><strong>{formatRemainingScans(remainingScans)}</strong></div>
          </div>

          {isScanLimitReached && (
            <div className="dbiz-coupon-notes dbiz-batch-notes">
              <span>Limit Reached</span>
              <p>This Dynamic QR batch has reached its scan limit.</p>
            </div>
          )}
        </>
      )}

      <section className="dbiz-table-card">
        <div className="dbiz-table-head">
          <div>
            <h2>QR codes in this batch</h2>
            <p>Individual QR status and scan analytics.</p>
          </div>
        </div>
        <Table rowKey="id" columns={columns} dataSource={qrCodes} scroll={{ x: 1200 }} pagination={{ pageSize: 10 }} />
      </section>
    </div>
  );
};

const unwrapArray = (responseData) => {
  const data = responseData?.data;
  if (Array.isArray(data)) return data;
  if (Array.isArray(data?.data)) return data.data;
  if (Array.isArray(data?.items)) return data.items;
  return [];
};

const normalizeId = (value) => (value === null || value === undefined ? "" : String(value));

const extractAssignedQrIdsFromStaff = (staffRecord = {}) => {
  const values = [
    staffRecord.assignedQrCodeIds,
    staffRecord.dynamicQrCodeIds,
    staffRecord.qrCodeIds,
    staffRecord.assignedQrCodes,
    staffRecord.dynamicQrCodes,
    staffRecord.assignments,
  ]
    .filter(Array.isArray)
    .flat()
    .map((item) => normalizeId(typeof item === "object" ? item.dynamicQrId ?? item.dynamicQrCodeId ?? item.qrCodeId ?? item.id : item))
    .filter(Boolean);

  return [...new Set(values)];
};

const extractDynamicQrIds = (record = {}) => {
  const values = [
    record.id,
    record.dynamicQrId,
    record.qrCodeId,
    record.qrCodes,
    record.data?.qrCodes,
  ]
    .filter((value) => value !== null && value !== undefined)
    .flat()
    .map((item) => normalizeId(typeof item === "object" ? item.id ?? item.dynamicQrId ?? item.dynamicQrCodeId ?? item.qrCodeId : item))
    .filter(Boolean);

  return [...new Set(values)];
};

const DynamicQrAccessPage = () => {
  const { message } = App.useApp();
  const navigate = useNavigate();
  const [activeTab, setActiveTab] = useState("staff");
  const [loading, setLoading] = useState(false);
  const [permissions, setPermissions] = useState(ACCESS_PERMISSION_OPTIONS);
  const [staff, setStaff] = useState([]);
  const [roles, setRoles] = useState([]);
  const [qrCodes, setQrCodes] = useState([]);
  const [staffModalOpen, setStaffModalOpen] = useState(false);
  const [roleModalOpen, setRoleModalOpen] = useState(false);
  const [assignmentModalOpen, setAssignmentModalOpen] = useState(false);
  const [editingStaff, setEditingStaff] = useState(null);
  const [editingRole, setEditingRole] = useState(null);
  const [selectedStaff, setSelectedStaff] = useState(null);
  const [staffForm, setStaffForm] = useState(initialStaffForm);
  const [roleForm, setRoleForm] = useState(initialRoleForm);
  const [assignedQrIds, setAssignedQrIds] = useState([]);
  const [reportType, setReportType] = useState("staff");
  const [reportPeriod, setReportPeriod] = useState("daily");
  const [reportStartDate, setReportStartDate] = useState("");
  const [reportEndDate, setReportEndDate] = useState("");
  const [reportRows, setReportRows] = useState([]);
  const accessBase = `${apiBase}/api/DynamicQr/access`;

  const roleOptions = roles.map((role) => ({
    value: role.id ?? role.roleId,
    label: role.roleName || role.name || `Role ${role.id ?? role.roleId}`,
  }));

  const qrOptions = qrCodes
    .filter((qr) => qr.rowType !== "Batch")
    .map((qr) => ({
      value: qr.id,
      label: `${qr.qrCodeName || qr.displayName || "Dynamic QR"}${qr.qrUrl ? ` - ${qr.qrUrl}` : ""}`,
    }));

  const fetchPermissions = async () => {
    try {
      const response = await axios.get(`${accessBase}/permissions`, { headers: authHeaders() });
      const keys = unwrapArray(response.data);
      if (keys.length > 0) {
        setPermissions(
          keys.map((item) => {
            const key = typeof item === "string" ? item : item.permissionKey || item.key || item.value;
            return {
              value: key,
              label: ACCESS_PERMISSION_OPTIONS.find((option) => option.value === key)?.label || key,
            };
          }).filter((item) => item.value)
        );
      }
    } catch {
      setPermissions(ACCESS_PERMISSION_OPTIONS);
    }
  };

  const fetchStaff = async () => {
    const response = await axios.get(`${accessBase}/staff`, { headers: authHeaders() });
    setStaff(unwrapArray(response.data));
  };

  const fetchRoles = async () => {
    const response = await axios.get(`${accessBase}/roles`, { headers: authHeaders() });
    setRoles(unwrapArray(response.data));
  };

  const fetchQrCodes = async () => {
    const response = await axios.get(`${apiBase}/api/DynamicQr`, {
      headers: authHeaders(),
      params: { page: 1, pageSize: 200 },
    });
    const result = response.data?.data || {};
    const data = Array.isArray(result.data) ? result.data : [];
    setQrCodes(data.map(normalizeDynamicQrRecord));
  };

  const fetchAccessData = async () => {
    setLoading(true);
    try {
      await Promise.all([fetchPermissions(), fetchStaff(), fetchRoles(), fetchQrCodes()]);
    } catch (error) {
      message.error(getApiMessage(error, "Unable to load access management data."));
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchAccessData();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const openStaffModal = (record = null) => {
    setEditingStaff(record);
    setStaffForm(record ? {
      ...initialStaffForm,
      firstName: record.firstName || "",
      lastName: record.lastName || "",
      mobileNumber: record.mobileNumber || "",
      email: record.email || "",
      password: "",
      autoGeneratePassword: false,
      forceChangePassword: Boolean(record.forceChangePassword),
      dateOfBirth: toDateInput(record.dateOfBirth),
      dateOfJoining: toDateInput(record.dateOfJoining),
      address: record.address || "",
      pinCode: record.pinCode || "",
      city: record.city || "",
      state: record.state || "",
      roleIds: Array.isArray(record.roleIds)
        ? record.roleIds
        : Array.isArray(record.roles)
          ? record.roles.map((role) => role.id ?? role.roleId).filter(Boolean)
          : [],
    } : initialStaffForm);
    setStaffModalOpen(true);
  };

  const openRoleModal = (record = null) => {
    setEditingRole(record);
    setRoleForm(record ? {
      roleName: record.roleName || record.name || "",
      description: record.description || "",
      permissionKeys: Array.isArray(record.permissionKeys)
        ? record.permissionKeys
        : Array.isArray(record.permissions)
          ? record.permissions.map((permission) => typeof permission === "string" ? permission : permission.permissionKey || permission.key).filter(Boolean)
          : [],
    } : initialRoleForm);
    setRoleModalOpen(true);
  };

  const saveStaff = async () => {
    if (!staffForm.firstName.trim() || !staffForm.email.trim()) {
      message.warning("First name and email are mandatory.");
      return;
    }
    if (!editingStaff && !staffForm.autoGeneratePassword && !staffForm.password.trim()) {
      message.warning("Password is mandatory unless auto-generate is enabled.");
      return;
    }

    const payload = {
      ...staffForm,
      firstName: staffForm.firstName.trim(),
      lastName: staffForm.lastName.trim(),
      mobileNumber: staffForm.mobileNumber.trim(),
      email: staffForm.email.trim(),
      password: staffForm.password.trim(),
      dateOfBirth: staffForm.dateOfBirth || null,
      dateOfJoining: staffForm.dateOfJoining || null,
      address: staffForm.address.trim(),
      pinCode: staffForm.pinCode.trim(),
      city: staffForm.city.trim(),
      state: staffForm.state.trim(),
      roleIds: staffForm.roleIds.map(Number),
    };

    if (payload.autoGeneratePassword || (editingStaff && !payload.password)) {
      delete payload.password;
    }

    try {
      const id = editingStaff?.id ?? editingStaff?.staffId;
      const response = editingStaff
        ? await axios.put(`${accessBase}/staff/${id}`, payload, { headers: authHeaders() })
        : await axios.post(`${accessBase}/staff`, payload, { headers: authHeaders() });
      const generatedPassword = response.data?.data?.generatedPassword;
      message.success(generatedPassword ? `Staff saved. Generated password: ${generatedPassword}` : "Staff saved.");
      setStaffModalOpen(false);
      fetchStaff();
    } catch (error) {
      message.error(getApiMessage(error, "Unable to save staff."));
    }
  };

  const deleteStaff = async (record) => {
    try {
      await axios.delete(`${accessBase}/staff/${record.id ?? record.staffId}`, { headers: authHeaders() });
      message.success("Staff deleted.");
      fetchStaff();
    } catch (error) {
      message.error(getApiMessage(error, "Unable to delete staff."));
    }
  };

  const saveRole = async () => {
    if (!roleForm.roleName.trim()) {
      message.warning("Role name is mandatory.");
      return;
    }
    const payload = {
      roleName: roleForm.roleName.trim(),
      description: roleForm.description.trim(),
      permissionKeys: roleForm.permissionKeys,
    };
    try {
      const id = editingRole?.id ?? editingRole?.roleId;
      if (editingRole) {
        await axios.put(`${accessBase}/roles/${id}`, payload, { headers: authHeaders() });
      } else {
        await axios.post(`${accessBase}/roles`, payload, { headers: authHeaders() });
      }
      message.success("Role saved.");
      setRoleModalOpen(false);
      fetchRoles();
    } catch (error) {
      message.error(getApiMessage(error, "Unable to save role."));
    }
  };

  const deleteRole = async (record) => {
    try {
      await axios.delete(`${accessBase}/roles/${record.id ?? record.roleId}`, { headers: authHeaders() });
      message.success("Role deleted.");
      fetchRoles();
    } catch (error) {
      message.error(getApiMessage(error, "Unable to delete role."));
    }
  };

  const openAssignmentModal = (record) => {
    setSelectedStaff(record);
    setAssignedQrIds(extractAssignedQrIdsFromStaff(record));
    setAssignmentModalOpen(true);
  };

  const saveAssignments = async () => {
    if (!selectedStaff) return;
    try {
      await axios.post(
        `${accessBase}/staff/${selectedStaff.id ?? selectedStaff.staffId}/assignments`,
        { qrCodeIds: assignedQrIds.map(Number) },
        { headers: authHeaders() }
      );
      message.success("QR assignments updated.");
      setAssignmentModalOpen(false);
      fetchStaff();
    } catch (error) {
      message.error(getApiMessage(error, "Unable to update assignments."));
    }
  };

  const fetchReport = async () => {
    const endpoint = reportType === "staff"
      ? `${accessBase}/reports/staff-analytics`
      : `${accessBase}/reports/qr-analytics`;
    try {
      const response = await axios.get(endpoint, {
        headers: authHeaders(),
        params: {
          period: reportPeriod,
          ...(reportStartDate && reportEndDate ? { startDate: reportStartDate, endDate: reportEndDate } : {}),
        },
      });
      setReportRows(unwrapArray(response.data));
      message.success("Report loaded.");
    } catch (error) {
      message.error(getApiMessage(error, "Unable to fetch report."));
    }
  };

  const staffColumns = [
    {
      title: "Staff",
      key: "staff",
      render: (_, record) => (
        <div>
          <strong className="dbiz-table-title">{`${record.firstName || ""} ${record.lastName || ""}`.trim() || record.email}</strong>
          <span className="dbiz-table-sub">{record.email}</span>
        </div>
      ),
    },
    { title: "Mobile", dataIndex: "mobileNumber", key: "mobileNumber" },
    {
      title: "Roles",
      key: "roles",
      render: (_, record) => {
        const names = Array.isArray(record.roles)
          ? record.roles.map((role) => role.roleName || role.name).filter(Boolean)
          : [];
        return names.length > 0 ? names.join(", ") : "-";
      },
    },
    {
      title: "Password",
      dataIndex: "forceChangePassword",
      key: "forceChangePassword",
      render: (value) => value ? <Tag color="orange">Change required</Tag> : <Tag color="green">Ready</Tag>,
    },
    {
      title: "Actions",
      key: "actions",
      render: (_, record) => (
        <div className="dbiz-actions">
          <button type="button" className="view" title="Assign QR" onClick={() => openAssignmentModal(record)}><EyeOutlined /></button>
          <button type="button" className="edit" title="Edit Staff" onClick={() => openStaffModal(record)}><EditOutlined /></button>
          <Popconfirm title="Delete staff?" onConfirm={() => deleteStaff(record)}>
            <button type="button" className="delete" title="Delete Staff"><DeleteOutlined /></button>
          </Popconfirm>
        </div>
      ),
    },
  ];

  const roleColumns = [
    { title: "Role", dataIndex: "roleName", key: "roleName", render: (value, record) => value || record.name },
    { title: "Description", dataIndex: "description", key: "description" },
    {
      title: "Permissions",
      key: "permissions",
      render: (_, record) => {
        const keys = Array.isArray(record.permissionKeys)
          ? record.permissionKeys
          : Array.isArray(record.permissions)
            ? record.permissions.map((permission) => typeof permission === "string" ? permission : permission.permissionKey || permission.key)
            : [];
        return <span className="dbiz-muted">{keys.filter(Boolean).join(", ") || "-"}</span>;
      },
    },
    {
      title: "Actions",
      key: "actions",
      render: (_, record) => (
        <div className="dbiz-actions">
          <button type="button" className="edit" title="Edit Role" onClick={() => openRoleModal(record)}><EditOutlined /></button>
          <Popconfirm title="Delete role?" onConfirm={() => deleteRole(record)}>
            <button type="button" className="delete" title="Delete Role"><DeleteOutlined /></button>
          </Popconfirm>
        </div>
      ),
    },
  ];

  const reportColumns = Array.from(
    new Set(reportRows.flatMap((row) => Object.keys(row || {})))
  ).map((key) => ({
    title: key.replace(/([A-Z])/g, " $1").replace(/^./, (char) => char.toUpperCase()),
    dataIndex: key,
    key,
    render: (value) => typeof value === "object" && value !== null ? JSON.stringify(value) : String(value ?? "-"),
  }));

  return (
    <div className="dbiz-root">
      <section className="dbiz-header">
        <div>
          <span className="dbiz-eyebrow">Business</span>
          <h1>Dynamic QR Access</h1>
          <p>Create staff users, define roles, assign QR codes, and review access reports.</p>
        </div>
        <div className="dbiz-header-actions">
          <Button onClick={fetchAccessData} loading={loading}>Refresh</Button>
        </div>
      </section>

      <Tabs
        activeKey={activeTab}
        onChange={setActiveTab}
        items={[
          {
            key: "staff",
            label: "Staff",
            children: (
              <section className="dbiz-table-card">
                <div className="dbiz-table-head">
                  <div>
                    <h2>Staff users</h2>
                    <p>Staff can use Dynamic QR APIs with their assigned permissions.</p>
                  </div>
                  <Button type="primary" icon={<PlusOutlined />} onClick={() => navigate("/dashboard/business/access/staff/new")}>
                    Add Staff
                  </Button>
                </div>
                <Table rowKey={(record) => record.id ?? record.staffId} loading={loading} columns={staffColumns} dataSource={staff} scroll={{ x: 980 }} />
              </section>
            ),
          },
          {
            key: "roles",
            label: "Roles",
            children: (
              <section className="dbiz-table-card">
                <div className="dbiz-table-head">
                  <div>
                    <h2>Roles</h2>
                    <p>Bundle permission keys into reusable access roles.</p>
                  </div>
                  <Button type="primary" icon={<PlusOutlined />} onClick={() => openRoleModal()}>
                    Add Role
                  </Button>
                </div>
                <Table rowKey={(record) => record.id ?? record.roleId} loading={loading} columns={roleColumns} dataSource={roles} scroll={{ x: 900 }} />
              </section>
            ),
          },
          {
            key: "reports",
            label: "Reports",
            children: (
              <section className="dbiz-table-card">
                <div className="dbiz-table-head">
                  <div>
                    <h2>Access reports</h2>
                    <p>Review staff-wise and QR-wise analytics.</p>
                  </div>
                  <Button type="primary" onClick={fetchReport}>Fetch Report</Button>
                </div>
                <div className="dbiz-filter-row">
                  <label>
                    Report
                    <Select value={reportType} onChange={setReportType} options={[{ value: "staff", label: "Staff-wise" }, { value: "qr", label: "QR-wise" }]} />
                  </label>
                  <label>
                    Period
                    <Select value={reportPeriod} onChange={setReportPeriod} options={[{ value: "daily", label: "Daily" }, { value: "monthly", label: "Monthly" }, { value: "yearly", label: "Yearly" }]} />
                  </label>
                  <label>
                    Start Date
                    <Input type="date" value={reportStartDate} onChange={(e) => setReportStartDate(e.target.value)} />
                  </label>
                  <label>
                    End Date
                    <Input type="date" value={reportEndDate} onChange={(e) => setReportEndDate(e.target.value)} />
                  </label>
                </div>
                <Table rowKey={(_, index) => index} columns={reportColumns} dataSource={reportRows} scroll={{ x: 900 }} />
              </section>
            ),
          },
        ]}
      />

      <Modal
        open={staffModalOpen}
        onCancel={() => setStaffModalOpen(false)}
        onOk={saveStaff}
        title={editingStaff ? "Edit Staff" : "Add Staff"}
        okText={editingStaff ? "Update Staff" : "Create Staff"}
        width={760}
        centered
      >
        <div className="dbiz-form-grid dbiz-form-grid-two">
          <label>First Name *<Input value={staffForm.firstName} onChange={(e) => setStaffForm((prev) => ({ ...prev, firstName: e.target.value }))} /></label>
          <label>Last Name<Input value={staffForm.lastName} onChange={(e) => setStaffForm((prev) => ({ ...prev, lastName: e.target.value }))} /></label>
          <label>Email *<Input value={staffForm.email} onChange={(e) => setStaffForm((prev) => ({ ...prev, email: e.target.value }))} /></label>
          <label>Mobile<Input value={staffForm.mobileNumber} onChange={(e) => setStaffForm((prev) => ({ ...prev, mobileNumber: e.target.value }))} /></label>
          <label>
            Auto-generate Password
            <Select
              value={staffForm.autoGeneratePassword ? "true" : "false"}
              onChange={(value) => setStaffForm((prev) => ({ ...prev, autoGeneratePassword: value === "true", password: value === "true" ? "" : prev.password }))}
              options={[{ value: "false", label: "No" }, { value: "true", label: "Yes" }]}
            />
          </label>
          <label>
            Force Password Change
            <Select
              value={staffForm.forceChangePassword ? "true" : "false"}
              onChange={(value) => setStaffForm((prev) => ({ ...prev, forceChangePassword: value === "true" }))}
              options={[{ value: "true", label: "Yes" }, { value: "false", label: "No" }]}
            />
          </label>
          {!staffForm.autoGeneratePassword && (
            <label className="dbiz-span-2">
              Password {editingStaff ? "" : "*"}
              <Input.Password value={staffForm.password} onChange={(e) => setStaffForm((prev) => ({ ...prev, password: e.target.value }))} />
            </label>
          )}
          <label>Date of Birth<Input type="date" value={staffForm.dateOfBirth} onChange={(e) => setStaffForm((prev) => ({ ...prev, dateOfBirth: e.target.value }))} /></label>
          <label>Date of Joining<Input type="date" value={staffForm.dateOfJoining} onChange={(e) => setStaffForm((prev) => ({ ...prev, dateOfJoining: e.target.value }))} /></label>
          <label className="dbiz-span-2">Address<Input value={staffForm.address} onChange={(e) => setStaffForm((prev) => ({ ...prev, address: e.target.value }))} /></label>
          <label>Pin Code<Input value={staffForm.pinCode} onChange={(e) => setStaffForm((prev) => ({ ...prev, pinCode: e.target.value }))} /></label>
          <label>City<Input value={staffForm.city} onChange={(e) => setStaffForm((prev) => ({ ...prev, city: e.target.value }))} /></label>
          <label>
            State
            <Select
              value={staffForm.state || undefined}
              onChange={(value) => setStaffForm((prev) => ({ ...prev, state: value }))}
              options={INDIAN_STATES}
              placeholder="Select state"
              showSearch
            />
          </label>
          <label className="dbiz-span-2">
            Roles
            <Select mode="multiple" value={staffForm.roleIds} onChange={(value) => setStaffForm((prev) => ({ ...prev, roleIds: value }))} options={roleOptions} placeholder="Select roles" />
          </label>
        </div>
      </Modal>

      <Modal
        open={roleModalOpen}
        onCancel={() => setRoleModalOpen(false)}
        onOk={saveRole}
        title={editingRole ? "Edit Role" : "Add Role"}
        okText={editingRole ? "Update Role" : "Create Role"}
        width={680}
        centered
      >
        <div className="dbiz-form-grid">
          <label className="dbiz-span-3">Role Name *<Input value={roleForm.roleName} onChange={(e) => setRoleForm((prev) => ({ ...prev, roleName: e.target.value }))} /></label>
          <label className="dbiz-span-3">Description<Input.TextArea rows={3} value={roleForm.description} onChange={(e) => setRoleForm((prev) => ({ ...prev, description: e.target.value }))} /></label>
          <label className="dbiz-span-3">
            Permissions
            <Select mode="multiple" value={roleForm.permissionKeys} onChange={(value) => setRoleForm((prev) => ({ ...prev, permissionKeys: value }))} options={permissions} placeholder="Select permissions" />
          </label>
        </div>
      </Modal>

      <Modal
        open={assignmentModalOpen}
        onCancel={() => setAssignmentModalOpen(false)}
        onOk={saveAssignments}
        title={`Assign QR Codes${selectedStaff?.email ? ` - ${selectedStaff.email}` : ""}`}
        okText="Save Assignments"
        width={720}
        centered
      >
        <label className="dbiz-modal-field">
          Dynamic QR Codes
          <Select
            mode="multiple"
            value={assignedQrIds}
            onChange={setAssignedQrIds}
            options={qrOptions.map((option) => ({ ...option, value: normalizeId(option.value) }))}
            placeholder="Select QR codes"
          />
        </label>
      </Modal>
    </div>
  );
};

const StaffCreatePage = () => {
  const { message } = App.useApp();
  const navigate = useNavigate();
  const [roles, setRoles] = useState([]);
  const [form, setForm] = useState(initialStaffForm);
  const [saving, setSaving] = useState(false);
  const [savingProgress, setSavingProgress] = useState(0);
  const [generatedCredentials, setGeneratedCredentials] = useState(null);
  const accessBase = `${apiBase}/api/DynamicQr/access`;

  useEffect(() => {
    const fetchRoles = async () => {
      try {
        const response = await axios.get(`${accessBase}/roles`, { headers: authHeaders() });
        setRoles(unwrapArray(response.data));
      } catch (error) {
        message.error(getApiMessage(error, "Unable to load roles."));
      }
    };

    fetchRoles();
  }, [accessBase, message]);

  const roleOptions = roles.map((role) => ({
    value: role.id ?? role.roleId,
    label: role.roleName || role.name || `Role ${role.id ?? role.roleId}`,
  }));

  const updateForm = (key, value) => {
    setForm((prev) => ({
      ...prev,
      [key]: value,
      ...(key === "autoGeneratePassword" && value ? { password: "" } : {}),
    }));
  };

  const copyText = async (value, label) => {
    if (!value) return;
    try {
      await navigator.clipboard.writeText(value);
      message.success(`${label} copied.`);
    } catch {
      message.error(`Unable to copy ${label.toLowerCase()}.`);
    }
  };

  const createStaff = async () => {
    if (!form.firstName.trim() || !form.email.trim()) {
      message.warning("First name and email are mandatory.");
      return;
    }
    if (!form.autoGeneratePassword && !form.password.trim()) {
      message.warning("Password is mandatory unless auto-generate is enabled.");
      return;
    }

    const payload = {
      ...form,
      firstName: form.firstName.trim(),
      lastName: form.lastName.trim(),
      mobileNumber: form.mobileNumber.trim(),
      email: form.email.trim(),
      password: form.password.trim(),
      dateOfBirth: form.dateOfBirth || null,
      dateOfJoining: form.dateOfJoining || null,
      address: form.address.trim(),
      pinCode: form.pinCode.trim(),
      city: form.city.trim(),
      state: form.state.trim(),
      roleIds: form.roleIds.map(Number),
    };

    if (payload.autoGeneratePassword) {
      delete payload.password;
    }

    setSaving(true);
    setSavingProgress(12);
    setGeneratedCredentials(null);
    const progressTimer = window.setInterval(() => {
      setSavingProgress((current) => Math.min(current + 8, 92));
    }, 450);
    try {
      const response = await axios.post(`${accessBase}/staff`, payload, { headers: authHeaders() });
      const nextGeneratedPassword = response.data?.data?.generatedPassword || "";
      setSavingProgress(100);
      if (!nextGeneratedPassword) {
        message.success("Staff created.");
        navigate("/dashboard/business/access");
        return;
      }
      setGeneratedCredentials({
        email: payload.email,
        password: nextGeneratedPassword,
      });
      message.success("Staff created.");
    } catch (error) {
      message.error(getApiMessage(error, "Unable to create staff."));
    } finally {
      window.clearInterval(progressTimer);
      setSaving(false);
    }
  };

  return (
    <div className="dbiz-root">
      <section className="dbiz-header dbiz-create-header">
        <div>
          <span className="dbiz-eyebrow">Access Management</span>
          <h1>Create Staff User</h1>
          <p>Add a staff member for Dynamic QR access and assign roles.</p>
        </div>
        <Button onClick={() => navigate("/dashboard/business/access")}>Back to Access</Button>
      </section>

      <section className="dbiz-create-card dbiz-staff-create-card">
        <div className="dbiz-form-section-head">
          <span>Staff Details</span>
          <h2>User information</h2>
        </div>
        {saving && (
          <div className="dbiz-staff-progress">
            <div>
              <strong>Creating staff user</strong>
              <span>Please wait while backend creates credentials.</span>
            </div>
            <Progress percent={savingProgress} status={savingProgress >= 100 ? "success" : "active"} />
          </div>
        )}
        <div className="dbiz-form-grid dbiz-form-grid-two dbiz-staff-form">
          <label>First Name *<Input value={form.firstName} onChange={(e) => updateForm("firstName", e.target.value)} placeholder="Enter first name" /></label>
          <label>Last Name<Input value={form.lastName} onChange={(e) => updateForm("lastName", e.target.value)} placeholder="Enter last name" /></label>
          <label>Email *<Input value={form.email} onChange={(e) => updateForm("email", e.target.value)} placeholder="staff@example.com" /></label>
          <label>Mobile Number<Input value={form.mobileNumber} onChange={(e) => updateForm("mobileNumber", e.target.value)} placeholder="9999999999" /></label>
          <label>
            Auto-generate Password
            <Select
              value={form.autoGeneratePassword ? "true" : "false"}
              onChange={(value) => updateForm("autoGeneratePassword", value === "true")}
              options={[{ value: "false", label: "No" }, { value: "true", label: "Yes" }]}
            />
          </label>
          <label>
            Force Password Change
            <Select
              value={form.forceChangePassword ? "true" : "false"}
              onChange={(value) => updateForm("forceChangePassword", value === "true")}
              options={[{ value: "true", label: "Yes" }, { value: "false", label: "No" }]}
            />
          </label>
          {!form.autoGeneratePassword && (
            <label className="dbiz-span-2">Password *<Input.Password value={form.password} onChange={(e) => updateForm("password", e.target.value)} placeholder="Enter secure password" /></label>
          )}
          <label>Date of Birth<Input type="date" value={form.dateOfBirth} onChange={(e) => updateForm("dateOfBirth", e.target.value)} /></label>
          <label>Date of Joining<Input type="date" value={form.dateOfJoining} onChange={(e) => updateForm("dateOfJoining", e.target.value)} /></label>
          <label className="dbiz-span-2">Address<Input value={form.address} onChange={(e) => updateForm("address", e.target.value)} placeholder="Street, area, landmark" /></label>
          <label>PIN Code<Input value={form.pinCode} onChange={(e) => updateForm("pinCode", e.target.value)} placeholder="110001" /></label>
          <label>City<Input value={form.city} onChange={(e) => updateForm("city", e.target.value)} placeholder="Delhi" /></label>
          <label>
            State
            <Select
              value={form.state || undefined}
              onChange={(value) => updateForm("state", value)}
              options={INDIAN_STATES}
              placeholder="Select state"
              showSearch
            />
          </label>
          <label className="dbiz-span-2">
            Roles
            <Select mode="multiple" value={form.roleIds} onChange={(value) => updateForm("roleIds", value)} options={roleOptions} placeholder="Select roles" />
          </label>
        </div>
        <div className="dbiz-create-actions">
          <Button onClick={() => navigate("/dashboard/business/access")}>Cancel</Button>
          <Button type="primary" loading={saving} onClick={createStaff}>Create Staff</Button>
        </div>
      </section>

      <Modal
        open={Boolean(generatedCredentials)}
        title="Staff Created"
        centered
        width={520}
        onCancel={() => setGeneratedCredentials(null)}
        footer={[
          <Button key="copy-password" icon={<CopyOutlined />} onClick={() => copyText(generatedCredentials?.password, "Password")}>
            Copy Password
          </Button>,
          <Button key="done" type="primary" onClick={() => navigate("/dashboard/business/access")}>
            Done
          </Button>,
        ]}
      >
        <div className="dbiz-generated-credentials">
          <p>Share these credentials securely with the staff member. The generated password is shown only once.</p>
          <div className="dbiz-generated-row">
            <span>Email</span>
            <strong>{generatedCredentials?.email}</strong>
            <Button size="small" icon={<CopyOutlined />} onClick={() => copyText(generatedCredentials?.email, "Email")} />
          </div>
          <div className="dbiz-generated-row">
            <span>Password</span>
            <strong>{generatedCredentials?.password}</strong>
            <Button size="small" icon={<CopyOutlined />} onClick={() => copyText(generatedCredentials?.password, "Password")} />
          </div>
        </div>
      </Modal>
    </div>
  );
};

const StaffChangePasswordPage = () => {
  const { message } = App.useApp();
  const navigate = useNavigate();
  const [currentPassword, setCurrentPassword] = useState("");
  const [newPassword, setNewPassword] = useState("");
  const [confirmPassword, setConfirmPassword] = useState("");
  const [loading, setLoading] = useState(false);

  const handleChangePassword = async () => {
    if (!currentPassword || !newPassword || !confirmPassword) {
      message.warning("Please fill all password fields.");
      return;
    }
    if (newPassword !== confirmPassword) {
      message.warning("New password and confirm password do not match.");
      return;
    }

    setLoading(true);
    try {
      const response = await axios.post(
        `${apiBase}/api/DynamicQr/access/staff/change-password`,
        { currentPassword, newPassword },
        { headers: authHeaders() }
      );
      const existingUser = getStoredUser();
      const updatedUser = {
        ...existingUser,
        ...(response.data?.data || {}),
        forceChangePassword: false,
      };
      localStorage.setItem("user", JSON.stringify(updatedUser));
      message.success("Password changed successfully.");
      navigate("/dashboard/business/dynamic-qr", { replace: true });
    } catch (error) {
      message.error(getApiMessage(error, "Unable to change password."));
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="dbiz-auth-shell">
      <section className="dbiz-auth-card">
        <span className="dbiz-eyebrow">Dynamic QR Staff</span>
        <h1>Change Password</h1>
        <p>Please change your temporary password before using the Dynamic QR module.</p>
        <label>Current Password<Input.Password value={currentPassword} onChange={(e) => setCurrentPassword(e.target.value)} /></label>
        <label>New Password<Input.Password value={newPassword} onChange={(e) => setNewPassword(e.target.value)} /></label>
        <label>Confirm Password<Input.Password value={confirmPassword} onChange={(e) => setConfirmPassword(e.target.value)} onPressEnter={handleChangePassword} /></label>
        <Button type="primary" block loading={loading} onClick={handleChangePassword}>Change Password</Button>
      </section>
    </div>
  );
};

const DashboardBusiness = ({ page }) => {
  if (page === "staff-change-password") return <StaffChangePasswordPage />;
  if (page === "staff-create") return <StaffCreatePage />;
  if (page === "coupons") return <CouponsPage />;
  if (page === "coupon-create") return <CouponCreatePage />;
  if (page === "coupon-batch") return <CouponBatchDetailPage />;
  if (page === "dynamic-qr") return <DynamicQrPage />;
  if (page === "dynamic-qr-create") return <DynamicQrCreatePage />;
  if (page === "dynamic-qr-batch") return <DynamicQrBatchDetailPage />;
  if (page === "access") return <DynamicQrAccessPage />;
  if (page === "pages") return <BusinessApiKeysPage />;

  const content = BUSINESS_PAGE_COPY[page] || BUSINESS_PAGE_COPY["dynamic-qr"];
  return (
    <div className="dbiz-root">
      <section className="dbiz-card">
        <span className="dbiz-eyebrow">Business</span>
        <h1>{content.title}</h1>
        <p>{content.description}</p>
        <div className="dbiz-note">This workspace is ready for the business feature flow.</div>
      </section>
    </div>
  );
};

export default DashboardBusiness;
